import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import TransactionsPieChart from './TransactionsPieChart';
import { buildCategoryTreeSorted } from './utils/expenseCategories';
import { exportToExcel } from './utils/exportUtils';
import AssetExpenseLinkModal from './components/AssetExpenseLinkModal.jsx';
import IncomeSplitModal from './components/IncomeSplitModal';
import IncomeLinkModal from './components/IncomeLinkModal.jsx';
import DebtDisbursementLinkModal from './components/DebtDisbursementLinkModal.jsx';
import Modal from './components/Modal';
import { useReceipts } from './hooks/useReceipts';
import ReceiptModal from './components/ReceiptModal';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { buildAssetLabel, getDomainForCategory } from './utils/assetLabelHelper';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SplitTransactionModal = ({ isOpen, transaction, onClose, onSaveSuccess, formatCurrency }) => {
  const [splitItems, setSplitItems] = useState([]);
  const [splitError, setSplitError] = useState('');
  const [splitSubmitting, setSplitSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && transaction) {
      const absAmt = Math.abs(Number(transaction.amount) || 0);
      const initial = absAmt > 0 ? [
        { key: 1, amount: Math.round((absAmt/2) * 100)/100, description: transaction.description },
        { key: 2, amount: Math.round((absAmt - Math.round((absAmt/2) * 100)/100) * 100)/100, description: transaction.description },
      ] : [{ key: 1, amount: 0, description: transaction.description }, { key: 2, amount: 0, description: transaction.description }];
      setSplitItems(initial);
      setSplitError('');
    }
  }, [isOpen, transaction]);

  if (!isOpen || !transaction) return null;

  const handleSave = async () => {
    setSplitError('');
    const parentAbs = Math.abs(Number(transaction.amount) || 0);
    const total = splitItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
    
    if (Math.abs(total - parentAbs) > 0.01) {
      setSplitError('Split amounts must sum exactly to the parent amount.');
      return;
    }
    if (splitItems.length < 2) {
      setSplitError('Add at least two split rows.');
      return;
    }

    setSplitSubmitting(true);
    try {
      const url = `/api/balance_sheet/assets/cash_accounts/transactions/${transaction.id}/split/`;
      const payload = { 
        splits: splitItems.map(it => ({ 
          amount: parseFloat(it.amount) || 0, 
          description: (it.description || '').trim() || null 
        })) 
      };
      await axios.post(url, payload);
      onSaveSuccess();
    } catch (e) {
      setSplitError('Failed to create split.');
    } finally {
      setSplitSubmitting(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }} onClick={()=> { if (!splitSubmitting) onClose(); }}>
      <div style={{ width:'min(720px, 95vw)', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }} onClick={(e)=> e.stopPropagation()}>
        <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
          <div style={{ fontSize:18, fontWeight:700 }}>Split Transaction</div>
          <button className="btn btn-sm btn-secondary" onClick={()=> { if (!splitSubmitting) onClose(); }} style={{ marginLeft:'auto' }}>✕</button>
        </div>
        <div style={{ padding:16 }}>
          {splitError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{splitError}</div>}
          <div style={{ marginBottom:8, color:'#9ca3af' }}>
            <div><strong>Date:</strong> {transaction.transaction_date || transaction.date || ''}</div>
            <div><strong>Description:</strong> {transaction.description || ''}</div>
            <div><strong>Parent Amount:</strong> {formatCurrency(Math.abs(Number(transaction.amount)||0))}</div>
            <small>Enter positive amounts for each child. They must sum to the parent amount.</small>
          </div>
          <table style={{ width:'100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign:'left', padding:8 }}>Child</th>
                <th style={{ textAlign:'right', padding:8, width:160 }}>Amount</th>
                <th style={{ textAlign:'left', padding:8 }}>Description</th>
                <th style={{ textAlign:'left', padding:8 }}>Remove</th>
              </tr>
            </thead>
            <tbody>
              {splitItems.map((it, idx) => (
                <tr key={it.key}>
                  <td style={{ padding:8 }}>#{idx+1}</td>
                  <td style={{ padding:8, textAlign:'right' }}>
                    <input type="number" step="0.01" className="form-control form-control-sm" value={it.amount}
                      onChange={(e)=> {
                        const v = e.target.value;
                        setSplitItems(prev => prev.map(x => x.key===it.key ? { ...x, amount: v } : x));
                      }} />
                  </td>
                  <td style={{ padding:8 }}>
                    <input type="text" className="form-control form-control-sm" value={it.description || ''}
                      onChange={(e)=> setSplitItems(prev => prev.map(x => x.key===it.key ? { ...x, description: e.target.value } : x))} />
                  </td>
                  <td style={{ padding:8 }}>
                    <button className="btn btn-sm btn-outline-secondary" onClick={()=> setSplitItems(prev => prev.filter(x => x.key !== it.key))} disabled={splitItems.length <= 2 || splitSubmitting}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="d-flex justify-content-between align-items-center" style={{ marginTop:12 }}>
            <button className="btn btn-outline-secondary btn-sm" onClick={()=> setSplitItems(prev => [...prev, { key: (prev[prev.length-1]?.key||0)+1, amount: 0, description: transaction.description }])} disabled={splitSubmitting}>+ Add Split</button>
            <div style={{ color:'#9ca3af' }}>
              Sum: {formatCurrency((splitItems.reduce((s,it)=> s + (parseFloat(it.amount)||0), 0)))} of {formatCurrency(Math.abs(Number(transaction.amount)||0))}
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={splitSubmitting}>Cancel</button>
            <button className="btn btn-success" disabled={splitSubmitting} onClick={handleSave}>{splitSubmitting ? 'Saving…' : 'Save Split'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CashAccountDetail = () => {
  const { id } = useParams();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const fileInputRef = React.useRef(null);

  // Balance series state
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesError, setSeriesError] = useState(null);
  const [period, setPeriod] = useState('1m');

  const filteredSeries = useMemo(() => {
    if (!series || series.length === 0) return [];
    if (period === 'All') return series;

    const now = new Date();
    let cutoff = new Date();

    if (period === '1w') cutoff.setDate(now.getDate() - 7);
    else if (period === '1m') cutoff.setDate(now.getDate() - 30);
    else if (period === '3m') cutoff.setMonth(now.getMonth() - 3);
    else if (period === '6m') cutoff.setMonth(now.getMonth() - 6);
    else if (period === '12m') cutoff.setFullYear(now.getFullYear() - 1);
    else if (period === 'YTD') cutoff = new Date(now.getFullYear(), 0, 1);

    return series.filter(p => new Date(p.date) >= cutoff);
  }, [series, period]);

  const averageBalance = useMemo(() => {
    if (!filteredSeries || filteredSeries.length === 0) return 0;
    const sum = filteredSeries.reduce((acc, p) => acc + p.balance, 0);
    return sum / filteredSeries.length;
  }, [filteredSeries]);

  const currentBalance = useMemo(() => {
    if (!series || series.length === 0) return 0;
    return series[series.length - 1].balance;
  }, [series]);

  // Asset list for resolving linked asset names/URLs
  const [allAssets, setAllAssets] = useState([]);
  useEffect(() => {
    const loadAssets = async () => {
      try {
        const phys = await axios.get('/api/balance_sheet/assets/physical_assets/');
        setAllAssets(Array.isArray(phys.data) ? phys.data : []);
      } catch (e) {
        setAllAssets([]);
      }
    };
    loadAssets();
  }, []);
  const buildAssetUrl = (asset) => {
    if (!asset) return null;
    const id = asset.id;
    const type2 = asset.asset_type_2 || asset.type_2 || asset.type2;
    const t = (type2 || '').toLowerCase();
    if (t === 'vehicle') return `/balance_sheet/assets/physical_assets/vehicles/${id}`;
    if (t === 'resi_property') return `/balance_sheet/assets/physical_assets/resi_properties/${id}`;
    if (t === 'structure' || t === 'land') {
      const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent;
      const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
      if (rpId) return `/balance_sheet/assets/physical_assets/resi_properties/${rpId}`;
    }
    return null;
  };

  // Memoized helper maps for fast asset lookups and labels
  const assetMaps = useMemo(() => {
    try {
      const list = Array.isArray(allAssets) ? allAssets : [];
      const byId = new Map();
      const resiAddressById = new Map(); // property id -> street address
      const structIdsByProperty = new Map(); // property id -> sorted structure ids
      for (const a of list) {
        const idStr = String(a.id);
        byId.set(idStr, a);
        const type2 = (a.asset_type_2 || a.type_2 || a.type2 || '').toLowerCase();
        if (type2 === 'resi_property') {
          const addr = a.address && a.address.street_address ? a.address.street_address : '';
          resiAddressById.set(idStr, addr);
        } else if (type2 === 'structure') {
          const rp = a.residential_property || a.residentialProperty || a.parent_property || a.parent || null;
          const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
          if (rpId != null) {
            const key = String(rpId);
            if (!structIdsByProperty.has(key)) structIdsByProperty.set(key, []);
            structIdsByProperty.get(key).push(a.id);
          }
        }
      }
      // Sort structure ids for ordinal positions
      const structOrdinalById = new Map();
      for (const ids of structIdsByProperty.values()) {
        ids.sort((a,b) => a - b);
        ids.forEach((sid, idx) => {
          structOrdinalById.set(String(sid), idx + 1);
        });
      }
      return { byId, resiAddressById, structOrdinalById };
    } catch (e) {
      return { byId: new Map(), resiAddressById: new Map(), structOrdinalById: new Map() };
    }
  }, [allAssets]);

  const buildStructureLabel = (asset) => buildAssetLabel(asset, assetMaps);

  // Transactions state
  // Income link modal state
  const [showIncomeLinkModal, setShowIncomeLinkModal] = useState(false);
  const [showDebtDisbursementModal, setShowDebtDisbursementModal] = useState(false);
  const [debtDisbursementRow, setDebtDisbursementRow] = useState(null);
  const [incomeLinkRow, setIncomeLinkRow] = useState(null);

  // Income split modal state
  const [showIncomeSplitModal, setShowIncomeSplitModal] = useState(false);
  const [incomeSplitRow, setIncomeSplitRow] = useState(null); // { id, amount, date, description, sourceType }
  const [incomeSplitDefaultKind, setIncomeSplitDefaultKind] = useState('non_recurring');

  // Transactions state
  const [rawTransactions, setRawTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState('');

  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitRow, setSplitRow] = useState(null); // { id, sourceType, amount, description, date }

  // Auto-assign categories
  const autoAssignCategories = async (transactions) => {
    try {
      if (!Array.isArray(transactions)) return;
      const getTime = (t) => {
        const s = t?.post_date || t?.transaction_date;
        const d = s ? new Date(s) : null;
        return d && !isNaN(d.getTime()) ? d.getTime() : 0;
      };
      const sorted = [...transactions].sort((a,b) => (getTime(a) - getTime(b)) || (Number(a.id) - Number(b.id)) );
      const lastByDesc = new Map();
      for (const t of sorted) {
        const desc = t?.description || '';
        if (!desc) continue;
        const currentCat = t?.expense_category || 'none';
        if (currentCat && currentCat !== 'none') { lastByDesc.set(desc, currentCat); continue; }
        const priorCat = lastByDesc.get(desc);
        if (priorCat && !autoCategorizedIds.has(t.id)) {
          try {
            await axios.patch(`${apiBase}/transactions/${t.id}/`, { expense_category: priorCat });
            setRawTransactions(prev => prev.map(x => x.id === t.id ? { ...x, expense_category: priorCat } : x));
            setAutoCategorizedIds(prev => { const ns = new Set(prev); ns.add(t.id); return ns; });
          } catch (e) { /* ignore */ }
        }
      }
    } catch (e) { /* ignore */ }
  };

  // Fetch transactions for this cash account (most recent 500 or all by account)
  const fetchTransactions = async (stmtOverride) => {
    if (!id) return;
    setTxLoading(true); setTxError('');
    const stmtId = stmtOverride !== undefined ? stmtOverride : selectedStatement;
    try {
      const qs = stmtId ? `?statement_id=${stmtId}` : '';
      const res = await axios.get(`${apiBase}/transactions/by-account/${id}/${qs}`);
      const list = Array.isArray(res.data) ? res.data : (res.data.transactions || []);
      setRawTransactions(list);
      try { await autoAssignCategories(list); } catch(e) { /* ignore */ }
    } catch (err) {
      setTxError('Failed to fetch transactions');
    } finally {
      setTxLoading(false);
    }
  };

  const {
    showReceiptModal,
    receiptPreviewUrl,
    receiptModalTitle,
    openReceipt,
    closeReceipt,
    uploadReceipt,
    deleteReceipt,
  } = useReceipts('/api/balance_sheet/assets/cash-accounts/transactions', fetchTransactions);

  // Sorting state
  const [sortField, setSortField] = useState('transaction_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Editing state
  const [editingCell, setEditingCell] = useState(null); // { transactionId, field }
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Deleting state
  const [deleting, setDeleting] = useState(new Set());
  // Utility linking modal state
  const [showUtilityModal, setShowUtilityModal] = useState(false);
  const [utilityRow, setUtilityRow] = useState(null);
  // Track which transactions were auto-categorized during this session
  const [autoCategorizedIds, setAutoCategorizedIds] = useState(new Set());

  // Expense categories
  const [expenseCategories, setExpenseCategories] = useState({ items: [], noneItem: { value: 'none', label: 'None' } });
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [expandedParents, setExpandedParents] = useState({});

  // Statements filter
  const [statements, setStatements] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState(null); // null = not yet determined

  // Income modal state
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [incomeKind, setIncomeKind] = useState(null); // 'non_recurring' | 'recurring'
  const [activeIncomeTxnId, setActiveIncomeTxnId] = useState(null);
  const [incomeStreams, setIncomeStreams] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(false);
  const [incomeForm, setIncomeForm] = useState({
    source: '',
    description: '',
    streamId: '',
    addNewStream: false,
    newStream: { source: '', description: '', start_date: '', end_date: '' },
  });

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [activePaymentTxnId, setActivePaymentTxnId] = useState(null);
  const [debtsForPayment, setDebtsForPayment] = useState([]);
  const [selectedDebt, setSelectedDebt] = useState({ type: '', id: '' });
  const [paymentCandidates, setPaymentCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [loadingPayment, setLoadingPayment] = useState(false);

  // Create Transaction modal state
  const [showCreateTxnModal, setShowCreateTxnModal] = useState(false);
  const [createTxnForm, setCreateTxnForm] = useState({
    transaction_date: '',
    post_date: '',
    description: '',
    amount: '',
    direction: 'debit', // 'debit' (money out) or 'deposit' (money in)
    expense_category: 'none',
  });
  const [createTxnError, setCreateTxnError] = useState('');
  const [creatingTxn, setCreatingTxn] = useState(false);

  const apiBase = '/api/balance_sheet/assets/cash_accounts';

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${apiBase}/${id}/`);
      setAccount(res.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  // Load expense categories (reuse credit card categories)
  useEffect(() => {
    const fetchExpenseCategories = async () => {
      try {
        const response = await axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/');
        const tree = buildCategoryTreeSorted(response.data || []);
        setExpenseCategories(tree);
      } catch (err) {
        // fallback to minimal set if needed
        setExpenseCategories({ items: [], noneItem: { value: 'none', label: 'None' } });
      }
    };
    fetchExpenseCategories();
  }, []);


  // Fetch statements for this account and auto-select default period
  const fetchStatements = async () => {
    if (!id) return;
    try {
      const res = await axios.get(`${apiBase}/statements/by-account/${id}/`);
      // Sort most recent first
      const list = (res.data || []).sort((a, b) => new Date(b.billing_cycle_end_date) - new Date(a.billing_cycle_end_date));
      setStatements(list);
      // Auto-select default: 'current' if there are post-statement transactions, else latest statement
      try {
        const currentRes = await axios.get(`${apiBase}/transactions/by-account/${id}/?statement_id=current`);
        const currentList = Array.isArray(currentRes.data) ? currentRes.data : (currentRes.data.transactions || []);
        let defaultStmt;
        if (currentList.length > 0) {
          defaultStmt = 'current';
        } else if (list.length > 0) {
          defaultStmt = String(list[0].id);
        } else {
          defaultStmt = '';
        }
        setSelectedStatement(defaultStmt);
        await fetchTransactions(defaultStmt);
      } catch (e) {
        // Fallback: default to latest statement
        const fallback = list.length > 0 ? String(list[0].id) : '';
        setSelectedStatement(fallback);
        await fetchTransactions(fallback);
      }
    } catch (e) {
      setStatements([]);
      setSelectedStatement('');
    }
  };

  useEffect(() => {
    setSelectedStatement(null); // Reset on account change so auto-select runs fresh
    fetchStatements();
  }, [id]);

  useEffect(() => {
    const fetchSeries = async () => {
      if (!id) return;
      setSeriesLoading(true); setSeriesError(null);
      try {
        const res = await axios.get(`${apiBase}/transactions/balance-series/${id}/`);
        setSeries(res.data.series || []);
      } catch (e) {
        setSeriesError(e.response?.data?.detail || e.message);
      } finally {
        setSeriesLoading(false);
      }
    };
    fetchSeries();
  }, [id]);

  const onUpload = async (selectedFiles = null, evt = null) => {
    if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
    setError(null);
    const filesToUpload = selectedFiles ? Array.from(selectedFiles) : (files || []);
    if (!filesToUpload || filesToUpload.length === 0) { setError('Please choose one or more PDF files.'); return; }

    // Build multipart with possibly multiple files
    const formData = new FormData();
    for (const f of filesToUpload) {
      formData.append('files', f);
    }
    formData.append('account_id', id);

    try {
      const res = await axios.post(`${apiBase}/transactions/upload_pdf_statements/`, formData);
      const data = res.data;
      
      // Success popup summary (single alert after all files processed)
      const errFiles = (data.files_processed || []).filter(x => x.status !== 'ok');
      const msg = [
        `Processed ${data.files_processed?.length || 0} file(s).`,
        `Summaries created/updated: ${data.total_statement_summaries_created_or_updated || 0}.`,
        `Transactions created: ${data.total_transactions_created || 0}.`,
        errFiles.length ? `Errors: ${errFiles.map(x => `${x.filename}: ${x.status}`).join('; ')}` : null,
      ].filter(Boolean).join('\n');
      window.alert(msg);
      // Refresh balance series after upload
      try {
        setSeriesLoading(true);
        const sres = await axios.get(`${apiBase}/transactions/balance-series/${id}/`);
        const sdata = sres.data;
        setSeries(sdata.series || []);
      } catch (err) {
        // ignore refresh errors
      } finally {
        setSeriesLoading(false);
      }
      // Refresh transactions for chart/table
      await fetchTransactions();
      // Clear the file input after success
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Upload failed');
    }
  };

  // Create new one-off cash transaction
  const submitCreateTransaction = async () => {
    if (!id) return;
    setCreateTxnError('');
    // Basic validation
    const d = (createTxnForm.description || '').trim();
    const tdate = createTxnForm.transaction_date;
    const amtStr = String(createTxnForm.amount || '').trim();
    if (!d || !tdate || !amtStr) {
      setCreateTxnError('Please provide transaction date, description, and amount.');
      return;
    }
    let amt = Number(amtStr);
    if (isNaN(amt) || !isFinite(amt)) {
      setCreateTxnError('Amount must be a valid number.');
      return;
    }
    // Apply direction (debit negative, deposit positive)
    if ((createTxnForm.direction || 'debit') === 'debit') {
      amt = -Math.abs(amt);
    } else {
      amt = Math.abs(amt);
    }
    const payload = {
      account: Number(id),
      transaction_date: createTxnForm.transaction_date,
      post_date: createTxnForm.post_date || createTxnForm.transaction_date,
      description: d,
      amount: amt,
      expense_category: createTxnForm.expense_category || 'none',
      source: 'user_supplied',
    };
    try {
      setCreatingTxn(true);
      await axios.post(`${apiBase}/transactions/`, payload);
      // Refresh series (balance chart) similar to upload flow
      try {
        setSeriesLoading(true);
        const sres = await axios.get(`${apiBase}/transactions/balance-series/${id}/`);
        setSeries(sres.data.series || []);
      } catch (e) { /* ignore */ }
      finally { setSeriesLoading(false); }
      // Refresh transactions and statements
      await fetchTransactions();
      await fetchStatements();
      // Close and reset form
      setShowCreateTxnModal(false);
      setCreateTxnForm({ transaction_date: '', post_date: '', description: '', amount: '', direction: 'debit', expense_category: 'none' });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to create transaction';
      setCreateTxnError(String(msg));
    } finally {
      setCreatingTxn(false);
    }
  };

  // Helpers
  const formatCurrency = (amount, decimalPlaces = 2) => {
    const num = Number(amount);
    const roundedAmount = Number(isNaN(num) ? 0 : num).toFixed(decimalPlaces);
    return `$${parseFloat(roundedAmount).toLocaleString(undefined, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
  };
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  // Category badge styling consistent with CreditCardDetail
  const getCategoryStyle = (category) => {
    const styles = {
      'food_dining': { backgroundColor: '#FFF3E0', color: '#E65100', borderColor: '#E65100' },
      'gas_fuel': { backgroundColor: '#F3E5F5', color: '#7B1FA2', borderColor: '#7B1FA2' },
      'groceries': { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
      'internet': { backgroundColor: '#E3F2FD', color: '#1565C0', borderColor: '#1565C0' },
      'apps': { backgroundColor: '#E8EAF6', color: '#5E35B1', borderColor: '#5E35B1' },
      'books': { backgroundColor: '#FEF3C7', color: '#78350F', borderColor: '#FBBF24' },
      'landscaping': { backgroundColor: '#E0F2E1', color: '#388E3C', borderColor: '#388E3C' },
      'shopping': { backgroundColor: '#FFE0E6', color: '#C2185B', borderColor: '#C2185B' },
      'entertainment': { backgroundColor: '#E1F5FE', color: '#0277BD', borderColor: '#0277BD' },
      'travel': { backgroundColor: '#FFF8E1', color: '#F57C00', borderColor: '#F57C00' },
      'automotive': { backgroundColor: '#E0F2F1', color: '#00695C', borderColor: '#00695C' },
      'healthcare': { backgroundColor: '#FFEBEE', color: '#C62828', borderColor: '#C62828' },
      'utilities': { backgroundColor: '#FFF3E0', color: '#EF6C00', borderColor: '#EF6C00' },
      'subscription': { backgroundColor: '#F3E5F5', color: '#8E24AA', borderColor: '#8E24AA' },
      'insurance': { backgroundColor: '#E8EAF6', color: '#3F51B5', borderColor: '#3F51B5' },
      'taxes': { backgroundColor: '#FFEBEE', color: '#B71C1C', borderColor: '#B71C1C' },
      'kids__other': { backgroundColor: '#E8F5E8', color: '#43A047', borderColor: '#43A047' },
      'education': { backgroundColor: '#E0F7FA', color: '#0097A7', borderColor: '#0097A7' },
      'business': { backgroundColor: '#ECEFF1', color: '#455A64', borderColor: '#455A64' },
      'personal_care': { backgroundColor: '#FCE4EC', color: '#AD1457', borderColor: '#AD1457' },
      'dental_products': { backgroundColor: '#FCE4EC', color: '#AD1457', borderColor: '#AD1457' },
      'workout_gym_products': { backgroundColor: '#F7FEE7', color: '#4D7C0F', borderColor: '#4D7C0F' },
      'home_improvement': { backgroundColor: '#F1F8E9', color: '#558B2F', borderColor: '#558B2F' },
      'gifts_charity': { backgroundColor: '#FFF9C4', color: '#9E9D24', borderColor: '#9E9D24' },
      'fees_charges': { backgroundColor: '#FFEBEE', color: '#D32F2F', borderColor: '#D32F2F' },
      'transfer': { backgroundColor: '#E3F2FD', color: '#1976D2', borderColor: '#1976D2' },
      'payments': { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
      'rewards_cashback': { backgroundColor: '#FFF8E1', color: '#F57C00', borderColor: '#F57C00' },
      'other': { backgroundColor: '#F5F5F5', color: '#616161', borderColor: '#616161' },
      'none': { backgroundColor: '#FFFFFF', color: '#000000', borderColor: '#000000' },
    };
    return styles[category] || styles['none'];
  };

  // Sorting handlers
  const ExcelIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
  );

  const handleExport = () => {
    const columns = [
      { header: 'Date', key: t => t.transaction_date ? new Date(t.transaction_date).toLocaleDateString() : '' },
      { header: 'Post Date', key: t => t.post_date ? new Date(t.post_date).toLocaleDateString() : '' },
      { header: 'Description', key: 'description' },
      { header: 'User Description', key: 'user_description' },
      { header: 'Amount', key: 'amount' },
      { header: 'Category', key: 'expense_category' },
      { header: 'Asset Link', key: t => t.linked_asset_label || '' },
      { header: 'Debt Link', key: t => t.linked_debt_label || '' }
    ];
    
    const accountName = account ? (account.account_name || account.name || 'CashAccount') : (id || 'CashAccount');
    const lastFour = account?.account_number ? account.account_number.slice(-4) : '';
    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '_');
    const filename = `Transactions_${accountName}${lastFour ? '_' + lastFour : ''}_${dateStr}`;
    exportToExcel(processedTransactions, filename, columns);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <span className="text-muted ms-1" style={{ cursor: 'pointer' }}>⇅</span>;
    }
    return (
      <span className="text-primary ms-1" style={{ cursor: 'pointer' }}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Precompute category label map for fast lookup
  const categoryLabelMap = useMemo(() => {
    const map = new Map();
    try {
      const tree = expenseCategories || { items: [], noneItem: { value: 'none', label: 'None' } };
      for (const it of (tree.items || [])) {
        map.set(String(it.value), String(it.label || it.value));
        if (Array.isArray(it.children)) {
          for (const ch of it.children) {
            map.set(String(ch.value), String(ch.label || ch.value));
          }
        }
      }
      if (tree.noneItem) map.set(String(tree.noneItem.value), String(tree.noneItem.label || 'None'));
    } catch (e) {}
    return map;
  }, [expenseCategories]);

  // Category label resolver for sorting by human label
  const getCategoryLabel = (val) => {
    const v = String(val);
    return categoryLabelMap.get(v) || (expenseCategories.noneItem?.label || 'None');
  };

  // Processed transactions (sorted) for charts and exports (flat, includes all raw rows)
  const processedTransactions = useMemo(() => {
    if (!rawTransactions || rawTransactions.length === 0) return [];

    const data = rawTransactions.map(t => {
      const asset = t.linked_asset_id ? (allAssets || []).find(a => String(a.id) === String(t.linked_asset_id)) : null;
      const assetLabel = asset ? buildAssetLabel(asset, { ...assetMaps, transactionDomain: getDomainForCategory(t.expense_category) }) : '';
      
      let debtLabel = '';
      const info = t.linked_debt_info || t.linkedDebt;
      if (info && (info.debt_label || info.debt_name)) {
          debtLabel = info.debt_label || info.debt_name || 'Debt';
      }

      return {
          ...t,
          linked_asset_label: assetLabel,
          linked_debt_label: debtLabel
      };
    });

    return [...data].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (sortField === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (sortField === 'transaction_date' || sortField === 'post_date') {
        aValue = aValue ? new Date(aValue) : null;
        bValue = bValue ? new Date(bValue) : null;
      } else if (sortField === 'expense_category') {
        aValue = getCategoryLabel(aValue || 'none').toLowerCase();
        bValue = getCategoryLabel(bValue || 'none').toLowerCase();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rawTransactions, sortField, sortDirection]);

  const hierarchy = useMemo(() => {
    const byId = new Map();
    const childrenByParent = new Map();
    const childIds = new Set();
    for (const t of (rawTransactions || [])) {
      byId.set(`cash-${t.id}`, t);
    }
    for (const t of (rawTransactions || [])) {
      const p = t.parent;
      if (p) {
        const key = `cash-${p}`;
        if (!childrenByParent.has(key)) childrenByParent.set(key, []);
        childrenByParent.get(key).push(t);
        childIds.add(`cash-${t.id}`);
      }
    }
    for (const [k, arr] of childrenByParent.entries()) {
      arr.sort((a,b) => {
        const ad = a.post_date || a.transaction_date; const bd = b.post_date || b.transaction_date;
        if (ad === bd) return a.id - b.id;
        return (ad < bd) ? -1 : 1;
      });
    }
    return { byId, childrenByParent, childIds };
  }, [rawTransactions]);

  // Transactions for the pie chart - should only include independent transactions and children (exclude parents)
  const chartTransactions = useMemo(() => {
    return (rawTransactions || []).filter(t => {
      const isChild = hierarchy.childIds.has(`cash-${t.id}`);
      const hasKids = !!(t.children_count || (hierarchy.childrenByParent.get(`cash-${t.id}`) || []).length > 0 || (Array.isArray(t.children) && t.children.length > 0));
      return isChild || !hasKids;
    });
  }, [rawTransactions, hierarchy]);

  const [expandedTxns, setExpandedTxns] = useState(new Set());

  const parentsOnlySorted = useMemo(() => {
    const parents = (rawTransactions || []).filter(r => !hierarchy.childIds.has(`cash-${r.id}`));
    return [...parents].sort((a,b) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (sortField === 'amount') { av = Number(av); bv = Number(bv); }
      else if (sortField === 'transaction_date' || sortField === 'post_date') { av = av ? new Date(av) : null; bv = bv ? new Date(bv) : null; }
      else if (sortField === 'expense_category') {
        av = getCategoryLabel(av || 'none').toLowerCase();
        bv = getCategoryLabel(bv || 'none').toLowerCase();
      }
      else if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      if (av == null && bv == null) return 0; if (av == null) return 1; if (bv == null) return -1;
      if (av < bv) return sortDirection === 'asc' ? -1 : 1; if (av > bv) return sortDirection === 'asc' ? 1 : -1; return 0;
    });
  }, [rawTransactions, hierarchy, sortField, sortDirection]);

  const processedTableRows = useMemo(() => {
    const out = [];
    for (const p of parentsOnlySorted) {
      out.push({ ...p, _isChild: false, has_children: !!(p.children_count || p.has_children || (Array.isArray(p.children) && p.children.length > 0)) });
      const key = `cash-${p.id}`;
      if (!expandedTxns.has(key)) continue;
      const kids = hierarchy.childrenByParent.get(key) || [];
      for (const kt of kids) {
        out.push({ ...kt, _isChild: true, parent: p.id, has_children: false });
      }
      // If no real cash child transactions, render synthetic children from API `children` field when present
      if (kids.length === 0 && Array.isArray(p.children) && p.children.length > 0) {
        for (const sc of p.children) {
          // sc fields come from backend serializer synthesis of asset_expense_splits
          out.push({
            ...sc,
            id: `synth-${p.id}-${sc.id}`,
            transaction_date: sc.transaction_date || sc.post_date || p.transaction_date,
            post_date: sc.post_date || sc.transaction_date || p.post_date,
            description: sc.description || 'Loan payment component',
            amount: Number(sc.amount || 0),
            // Keep raw code for potential future logic, and carry human label separately
            expense_category: sc.expense_category || 'none',
            category_label: sc.category_label || sc.expense_category || 'None',
            read_only_category: !!sc.read_only_category,
            available_expense_categories: Array.isArray(sc.available_expense_categories) ? sc.available_expense_categories : [],
            linked_asset_id: sc.linked_asset_id || sc.asset_id || sc.asset || '',
            linked_asset_expense_id: sc.linked_asset_expense_id || sc.id || '',
            linked_utility_bill_id: sc.linked_utility_bill_id || sc.utility_bill_id || sc.utility_bill || null,
            linked_recurring_series_id: sc.linked_recurring_series_id || sc.recurring_series_id || sc.recurring_series || null,
            parent: p.id,
            _isChild: true,
            has_children: false,
          });
        }
      }
    }
    return out;
  }, [parentsOnlySorted, expandedTxns, hierarchy]);

  const parentRowKeysWithChildren = useMemo(() => {
    return parentsOnlySorted.filter(r => (r.children_count > 0) || r.has_children || (Array.isArray(r.children) && r.children.length > 0)).map(r => `cash-${r.id}`);
  }, [parentsOnlySorted]);
  const toggleAllTxnParents = () => {
    const allOpen = parentRowKeysWithChildren.every(k => expandedTxns.has(k));
    setExpandedTxns(allOpen ? new Set() : new Set(parentRowKeysWithChildren));
  };

  // Inline editing
  const startEditing = (transactionId, field, currentValue) => {
    setEditingCell({ transactionId, field });
    if (field === 'transaction_date' || field === 'post_date') {
      const date = currentValue ? new Date(currentValue) : null;
      setEditValue(date ? date.toISOString().split('T')[0] : '');
    } else if (field === 'amount') {
      setEditValue((currentValue ?? '').toString());
    } else {
      setEditValue(currentValue || '');
    }
  };
  const cancelEditing = () => { setEditingCell(null); setEditValue(''); };
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') { saveEdit(); }
    else if (e.key === 'Escape') { cancelEditing(); }
  };
  const saveEdit = async () => {
    if (!editingCell || saving) return;
    setSaving(true);
    try {
      let valueToSave = editValue;
      if (editingCell.field === 'amount') {
        const numValue = parseFloat(editValue);
        if (isNaN(numValue)) { alert('Please enter a valid number for amount'); setSaving(false); return; }
        valueToSave = numValue;
      } else if (editingCell.field === 'transaction_date' || editingCell.field === 'post_date') {
        const dateValue = new Date(editValue);
        if (isNaN(dateValue.getTime())) { alert('Please enter a valid date'); setSaving(false); return; }
        valueToSave = editValue;
      } else if (editingCell.field === 'description') {
        if (!editValue.trim()) { alert('Description cannot be empty'); setSaving(false); return; }
        valueToSave = editValue.trim();
      }
      await axios.patch(`${apiBase}/transactions/${editingCell.transactionId}/`, { [editingCell.field]: valueToSave });
      setRawTransactions(prev => prev.map(t => t.id === editingCell.transactionId ? { ...t, [editingCell.field]: valueToSave } : t));
      setEditingCell(null); setEditValue('');
    } catch (err) {
      alert('Failed to save changes.');
    } finally { setSaving(false); }
  };

  const deleteTransaction = async (transactionId) => {
    if (!window.confirm('Delete this transaction?')) return;
    setDeleting(prev => new Set([...prev, transactionId]));
    try {
      await axios.delete(`${apiBase}/transactions/${transactionId}/`);
      setRawTransactions(prev => prev.filter(t => t.id !== transactionId));
    } catch (err) {
      alert('Failed to delete transaction.');
    } finally {
      setDeleting(prev => { const ns = new Set(prev); ns.delete(transactionId); return ns; });
    }
  };

  const loadIncomeStreams = async () => {
    setLoadingStreams(true);
    try {
      const res = await axios.get(`${apiBase}/transactions/income/streams/`);
      setIncomeStreams(res.data || []);
    } catch (e) {
      setIncomeStreams([]);
    } finally {
      setLoadingStreams(false);
    }
  };

  // Vehicle linking modal state (cash)
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [autoTxnId, setAutoTxnId] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [assetExpenseChoices, setAssetExpenseChoices] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [autoForm, setAutoForm] = useState({
    vehicleId: '', assetExpenseCategory: 'maintenance', isRecurring: false,
    scheduleMode: 'existing', scheduleId: '',
    newSchedule: { description: '', frequency: 'monthly', expected_date: '', expected_amount: '' },
    loading: false, error: ''
  });

  const openAutoModal = async (transactionId) => {
    setAutoTxnId(transactionId);
    const txn = (rawTransactions || []).find(t => t.id === transactionId) || null;
    const amt = txn ? Math.abs(Number(typeof txn.amount === 'object' && txn.amount.amount != null ? txn.amount.amount : txn.amount)) : '';
    const dt = txn ? (txn.transaction_date || txn.post_date) : new Date().toISOString().slice(0,10);
    setAutoForm(prev => ({ ...prev, vehicleId:'', assetExpenseCategory:'maintenance', isRecurring:false, scheduleMode:'existing', scheduleId:'',
      newSchedule: { description: txn?.description || '', frequency:'monthly', expected_date: dt, expected_amount: amt }, loading:true, error:'' }));
    setSchedules([]);
    setShowAutoModal(true);
    try {
      const phys = await axios.get('/api/balance_sheet/assets/physical_assets/');
      setVehicles((Array.isArray(phys.data) ? phys.data : []).filter(a => (a.asset_type_2 || '').toLowerCase()==='vehicle'));
      const aec = await axios.get('/api/balance_sheet/assets/asset-expense-category-choices/');
      setAssetExpenseChoices(Array.isArray(aec.data) ? aec.data.map(([v,l])=>({value:v,label:l})) : []);
    } catch (e) { setAutoForm(prev=> ({ ...prev, error:'Failed to load vehicles or categories.'})); }
    finally { setAutoForm(prev=> ({ ...prev, loading:false })); }
  };

  const onChangeVehicle = async (vid) => {
    setAutoForm(prev=> ({ ...prev, vehicleId: vid, loading:true, error:'', scheduleId:'' }));
    setSchedules([]);
    if (!vid) { setAutoForm(prev=> ({ ...prev, loading:false })); return; }
    try { const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${vid}/maintenance-items/`); setSchedules(Array.isArray(res.data)? res.data: []); }
    catch (e) { setSchedules([]); }
    finally { setAutoForm(prev=> ({ ...prev, loading:false })); }
  };

  const submitAutoLink = async () => {
    if (!autoTxnId || !autoForm.vehicleId) { setAutoForm(prev=> ({ ...prev, error:'Please select a vehicle.' })); return; }
    setAutoForm(prev=> ({ ...prev, loading:true, error:'' }));
    let schedId = '';
    try {
      if (autoForm.isRecurring && autoForm.scheduleMode==='new') {
        const payload = { asset:Number(autoForm.vehicleId), description:autoForm.newSchedule.description || (rawTransactions.find(t=>t.id===autoTxnId)?.description)||'Recurring vehicle expense', recurring:true, recurring_frequency:autoForm.newSchedule.frequency, expected_maintenance_date:autoForm.newSchedule.expected_date, expected_expense_amount:Number(autoForm.newSchedule.expected_amount)||0 };
        const cr = await axios.post('/api/balance_sheet/assets/physical_assets/add-maintenance-item/', payload); schedId = cr?.data?.id;
      } else if (autoForm.isRecurring && autoForm.scheduleMode==='existing') {
        schedId = autoForm.scheduleId || '';
      }
      await axios.post(`/api/balance_sheet/assets/cash_accounts/transactions/${autoTxnId}/link-asset-expense/`, {
        asset_id: Number(autoForm.vehicleId), expense_category: autoForm.assetExpenseCategory || 'maintenance', schedule_id: schedId || undefined,
        description: (rawTransactions.find(t=>t.id===autoTxnId)?.description) || undefined,
      });
      setShowAutoModal(false); setAutoTxnId(null);
      // Optional refresh
      await fetchTransactions();
    } catch (e) { setAutoForm(prev=> ({ ...prev, error:'Failed to link vehicle expense.' })); }
    finally { setAutoForm(prev=> ({ ...prev, loading:false })); }
  };

  // Landscaping modal state
  const [showLandModal, setShowLandModal] = useState(false);
  const [landTxnId, setLandTxnId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [landChild, setLandChild] = useState(null);
  const [landLoading, setLandLoading] = useState(false);
  const [landError, setLandError] = useState('');
  // Add Land form state
  const [showAddLand, setShowAddLand] = useState(false);
  const [landFormLoading, setLandFormLoading] = useState(false);
  const [landFormError, setLandFormError] = useState('');
  const [landForm, setLandForm] = useState({
    legal_description: '',
    parcel_number: '',
    lot_number: '',
    plat_number: '',
    tax_district: '',
    area_in_acres: '',
    latitude: '',
    longitude: '',
    survey_description: '',
    financing_allocation: ''
  });
  // Recurring settings for Landscaping expenses
  const [landIsRecurring, setLandIsRecurring] = useState(false);
  const [landFrequency, setLandFrequency] = useState('monthly');
  const [landRecurringOptions, setLandRecurringOptions] = useState([]);
  const [landRecurringLoading, setLandRecurringLoading] = useState(false);
  const [landRecurringChoice, setLandRecurringChoice] = useState(''); // existing id or '__new__'
  const [landRecurringName, setLandRecurringName] = useState('');

  const openLandModal = async (transactionId) => {
    setLandTxnId(transactionId);
    setShowLandModal(true);
    setSelectedPropertyId('');
    setLandChild(null);
    setLandError('');
    setLandLoading(true);
    try {
      const res = await axios.get('/api/balance_sheet/assets/physical_assets/property-choices/');
      setProperties(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setProperties([]);
      setLandError('Failed to load properties.');
    } finally {
      setLandLoading(false);
    }
  };
  const onChangePropertyForLand = async (pid) => {
    setSelectedPropertyId(pid);
    setLandChild(null);
    if (!pid) return;
    setLandLoading(true);
    setLandError('');
    try {
      const opts = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
      const children = (opts.data && Array.isArray(opts.data.children)) ? opts.data.children : [];
      const land = children.find(ch => (ch.type || '').toLowerCase() === 'land');
      const selected = land ? { id: land.id, label: land.label } : null;
      setLandChild(selected);
      // If recurring mode is enabled, fetch existing series for this land asset
      if (selected && landIsRecurring) {
        await fetchLandRecurringOptions(selected.id);
      }
    } catch (e) {
      setLandError('Failed to load property details.');
      setLandChild(null);
    } finally {
      setLandLoading(false);
    }
  };
  const fetchLandRecurringOptions = async (assetId) => {
    // For canon series, fetch by property with include_children=1 so land series are visible
    if (!selectedPropertyId) { setLandRecurringOptions([]); return; }
    try {
      setLandRecurringLoading(true);
      const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${selectedPropertyId}/recurring-series/`, { params: { include_children: 1, include_inactive: 1 } });
      const items = Array.isArray(res.data) ? res.data.map(s => ({ id: String(s.id), name: s.name })) : [];
      setLandRecurringOptions(items);
    } catch (e) {
      setLandRecurringOptions([]);
    } finally {
      setLandRecurringLoading(false);
    }
  };

  const submitLandLink = async () => {
    if (!landTxnId || !landChild) { setLandError('Please select property with Land.'); return; }
    setLandLoading(true);
    setLandError('');
    try {
      // Resolve recurring series id via canonical series (property-scoped)
      let recurringSeriesId = null;
      if (landIsRecurring) {
        if (landRecurringChoice === '__new__') {
          const txn = (rawTransactions || []).find(t => t.id === landTxnId) || {};
          const name = (landRecurringName && landRecurringName.trim().length ? landRecurringName.trim() : (txn.description || 'Recurring landscaping expense'));
          const cr = await axios.post(`/api/balance_sheet/assets/physical_assets/${selectedPropertyId}/recurring-series/manage/`, {
            action: 'create', name, recurring_type: 'variable', recurring_frequency: landFrequency, include_children: 1
          });
          if (cr?.data?.id != null) recurringSeriesId = Number(cr.data.id);
        } else if (landRecurringChoice) {
          recurringSeriesId = Number(landRecurringChoice);
        }
      }
      await axios.post(`${apiBase}/transactions/${landTxnId}/link-asset-expense/`, {
        asset_id: Number(landChild.id),
        expense_category: 'landscaping',
        // Recurring metadata
        recurring: landIsRecurring,
        recurring_frequency: landIsRecurring && landRecurringChoice === '__new__' ? landFrequency : null,
        recurring_name: landIsRecurring && landRecurringChoice === '__new__' ? (landRecurringName || undefined) : undefined,
        recurring_series_id: recurringSeriesId != null ? recurringSeriesId : undefined,
      });
      setShowLandModal(false);
      setLandTxnId(null);
      await fetchTransactions();
    } catch (e) {
      setLandError('Failed to link expense.');
    } finally {
      setLandLoading(false);
    }
  };

  // Home (Property/Structure) modal state and handlers
  const [showHomeModal, setShowHomeModal] = useState(false);
  const [homeTxnId, setHomeTxnId] = useState(null);
  const [homeUiCategory, setHomeUiCategory] = useState('');
  const [homeLoading, setHomeLoading] = useState(false);
  const [homeError, setHomeError] = useState('');
  const [homeProperties, setHomeProperties] = useState([]);
  const [homeSelectedPropertyId, setHomeSelectedPropertyId] = useState('');
  const [homeAssetOptions, setHomeAssetOptions] = useState([]);
  const [homeSelectedAssetId, setHomeSelectedAssetId] = useState('');
  const [homeIsRecurring, setHomeIsRecurring] = useState(false);
  const [homeFrequency, setHomeFrequency] = useState('monthly');
  const [homeSeriesOptions, setHomeSeriesOptions] = useState([]);
  const [homeRecurringMode, setHomeRecurringMode] = useState('existing'); // 'existing' | 'new'
  const [homeExistingSeriesName, setHomeExistingSeriesName] = useState('');
  const [homeRecurringName, setHomeRecurringName] = useState('');
  const [homeRecurringType, setHomeRecurringType] = useState('variable');
  const [homeRecurringChoice, setHomeRecurringChoice] = useState(''); // existing id or '__new__'
  const [homeRecurringLoading, setHomeRecurringLoading] = useState(false);

  const HOME_UI_TO_ASSET = {
    home_repair: 'repair',
    home_maintenance: 'maintenance',
    home_enhancement: 'enhancement',
    home_decor: 'home_decor',
    home_furniture: 'home_furniture',
    home_artwork: 'home_artwork',
    home_pest_control: 'pest_control',
    home_inspection: 'home_inspection',
    home_tax_payment: 'taxes',
    home_insurance_payment: 'insurance',
    home_other: 'home_other',
  };

  const openHomeModal = async (transactionId, uiCategory) => {
    setHomeTxnId(transactionId);
    setHomeUiCategory(uiCategory);
    setShowHomeModal(true);
    setHomeLoading(true);
    setHomeError('');
    setHomeSelectedPropertyId('');
    setHomeAssetOptions([]);
    setHomeSelectedAssetId('');
    try {
      const res = await axios.get('/api/balance_sheet/assets/physical_assets/property-choices/');
      setHomeProperties(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setHomeProperties([]);
      setHomeError('Failed to load properties.');
    } finally {
      setHomeLoading(false);
    }
  };

  const onChangePropertyForHome = async (pid) => {
    setHomeSelectedPropertyId(pid);
    setHomeAssetOptions([]);
    setHomeSelectedAssetId('');
    setHomeRecurringChoice('');
    setHomeSeriesOptions([]);
    if (!pid) return;
    setHomeLoading(true);
    setHomeError('');
    try {
      // Use enriched linkage-options to include the property itself, structures, and land
      const resp = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
      const root = resp?.data || {};
      const children = Array.isArray(root.children) ? root.children : [];
      const options = [];
      // Entire property option
      if (root.id) {
        const plabel = root.label || (root.address ? `Residential Property - ${root.address}` : `Residential Property - ${pid}`);
        options.push({ id: root.id, label: plabel });
      }
      // All child assets (structures and land)
      for (const ch of children) {
        if (!ch || !ch.id) continue;
        const lbl = ch.label || (ch.type ? `${String(ch.type).charAt(0).toUpperCase() + String(ch.type).slice(1)} ${ch.id}` : `Asset ${ch.id}`);
        options.push({ id: ch.id, label: lbl });
      }
      setHomeAssetOptions(options);
      if (!options.length) {
        setHomeError('No linkable assets found for the selected property. Please add a Structure or Land first.');
      }
    } catch (e) {
      setHomeError('Failed to load property details.');
      setHomeAssetOptions([]);
    } finally {
      setHomeLoading(false);
    }
  };

  const submitHomeLink = async () => {
    if (!homeTxnId || !homeSelectedAssetId || !homeUiCategory) {
      setHomeError('Please select a property and asset.');
      return;
    }
    setHomeLoading(true);
    setHomeError('');
    try {
      const assetCat = HOME_UI_TO_ASSET[homeUiCategory] || 'maintenance';
      let scheduleId = '';
      if (homeIsRecurring) {
        if (homeRecurringChoice === '__new__') {
          const txn = (rawTransactions || []).find(t => t.id === homeTxnId) || {};
          const txAmt = (()=>{ try { return Math.abs(Number(typeof txn.amount === 'object' && txn.amount?.amount != null ? txn.amount.amount : txn.amount)); } catch(e) { return 0; } })();
          const txDate = (txn.transaction_date || txn.post_date || new Date().toISOString().slice(0,10));
          const payload = {
            asset: Number(homeSelectedAssetId),
            description: (homeRecurringName && homeRecurringName.trim().length ? homeRecurringName : (txn.description || 'Recurring home expense')),
            recurring: true,
            recurring_frequency: homeFrequency,
            expected_maintenance_date: txDate,
            expected_expense_amount: Number(txAmt) || 0
          };
          const cr = await axios.post('/api/balance_sheet/assets/physical_assets/add-maintenance-item/', payload);
          scheduleId = cr?.data?.id;
        } else if (homeRecurringChoice) {
          scheduleId = homeRecurringChoice;
        }
      }
      await axios.post(`${apiBase}/transactions/${homeTxnId}/link-asset-expense/`, {
        asset_id: Number(homeSelectedAssetId),
        expense_category: assetCat,
        schedule_id: scheduleId || undefined,
        recurring: homeIsRecurring,
        recurring_frequency: homeIsRecurring ? homeFrequency : null,
        recurring_name: (homeRecurringChoice === '__new__') ? (homeRecurringName || undefined) : undefined,
      });
      setShowHomeModal(false);
      setHomeTxnId(null);
      await fetchTransactions();
    } catch (e) {
      setHomeError('Failed to link home expense.');
    } finally {
      setHomeLoading(false);
    }
  };

  const submitAddLand = async () => {
    if (!selectedPropertyId) { setLandFormError('Please select a property first.'); return; }
    setLandFormError('');
    setLandFormLoading(true);
    try {
      const payload = {
        asset_type_2: 'land',
        residential_property: Number(selectedPropertyId),
        legal_description: landForm.legal_description,
        parcel_number: landForm.parcel_number,
        lot_number: landForm.lot_number ? Number(landForm.lot_number) : null,
        plat_number: landForm.plat_number ? Number(landForm.plat_number) : null,
        tax_district: landForm.tax_district,
        area_in_acres: landForm.area_in_acres ? Number(landForm.area_in_acres) : null,
        latitude: landForm.latitude || null,
        longitude: landForm.longitude || null,
        survey_description: landForm.survey_description || '',
        financing_allocation: landForm.financing_allocation ? Number(landForm.financing_allocation) : null
      };
      const res = await axios.post('/api/balance_sheet/assets/physical_assets/resi_properties/land/create-land-asset/', payload);
      const created = res.data;
      const label = created && created.area_in_acres ? `Land — ${created.area_in_acres} acres` : 'Land';
      setLandChild({ id: created.id, label });
      setShowAddLand(false);
    } catch (e) {
      setLandFormError('Failed to create Land. Please check inputs.');
    } finally {
      setLandFormLoading(false);
    }
  };

  const updateExpenseCategory = async (transactionId, category) => {
    // Handle synthetic IDs (e.g., synth-123-456) for split child transactions
    let realId = transactionId;
    if (typeof transactionId === 'string' && transactionId.startsWith('synth-')) {
      const parts = transactionId.split('-');
      if (parts.length >= 3) {
        realId = parts[parts.length - 1];
        console.log(`Resolved synthetic ID ${transactionId} to real ID ${realId}`);
      }
    }

    try {
      await axios.patch(`${apiBase}/transactions/${realId}/`, { expense_category: category });

      // Update local state - update rawTransactions since that's our source
      setRawTransactions(prev => prev.map(transaction => {
        if (transaction.id === transactionId) {
          return { ...transaction, expense_category: category };
        }
        if (transaction.children && transaction.children.length > 0) {
          return {
            ...transaction,
            children: transaction.children.map(child =>
              child.id === transactionId || String(child.id) === String(realId)
                ? { ...child, expense_category: category }
                : child
            )
          };
        }
        return transaction;
      }));
      setOpenDropdownId(null);
      // If income selected (including deprecated categories), open the new income link modal
      if (category === 'income' || category === 'income_nonrecurring' || category === 'income_recurring' || category === 'tax_refund') {
        try {
          const txn = rawTransactions.find(t => t.id === transactionId);
          if (txn) {
            setIncomeLinkRow({ ...txn, date: txn.transaction_date });
            setShowIncomeLinkModal(true);
          }
        } catch (e) { /* ignore */ }
      }
      // If automotive or gas & fuel, open vehicle linking modal
      if (category === 'automotive' || category === 'gas_fuel') {
        const txn = rawTransactions.find(t => t.id === transactionId);
        if (txn) {
          setUtilityRow({
            id: txn.id,
            sourceType: 'cash',
            date: txn.transaction_date,
            post_date: txn.post_date,
            description: txn.description,
            amount: txn.amount,
            linked_asset_id: txn.linked_asset_id || '',
            linked_asset_expense_id: txn.linked_asset_expense_id || '',
            linked_recurring_series_id: txn.linked_recurring_series_id || '',
            expense_category: category,
          });
          setShowUtilityModal(true);
        }
      }
      // If Landscaping, open property/land modal
      if (category === 'landscaping') {
        const txn = rawTransactions.find(t => t.id === transactionId);
        if (txn) {
          setUtilityRow({
            id: txn.id,
            sourceType: 'cash',
            date: txn.transaction_date,
            post_date: txn.post_date,
            description: txn.description,
            amount: txn.amount,
            linked_asset_id: txn.linked_asset_id || '',
            linked_asset_expense_id: txn.linked_asset_expense_id || '',
            linked_recurring_series_id: txn.linked_recurring_series_id || '',
            expense_category: category,
          });
          setShowUtilityModal(true);
        }
      }
      // If Home child selected, open property/structure modal
      if ([
        'home_repair','home_maintenance','home_enhancement','home_decor','home_furniture','home_artwork','home_pest_control',
        'home_inspection','home_insurance_payment', 'home_plants'
      ].includes(category)) {
        const txn = rawTransactions.find(t => t.id === transactionId);
        if (txn) {
          setUtilityRow({
            id: txn.id,
            sourceType: 'cash',
            date: txn.transaction_date,
            post_date: txn.post_date,
            description: txn.description,
            amount: txn.amount,
            linked_asset_id: txn.linked_asset_id || '',
            linked_asset_expense_id: txn.linked_asset_expense_id || '',
            linked_recurring_series_id: txn.linked_recurring_series_id || '',
            expense_category: category,
          });
          setShowUtilityModal(true);
        }
      }
      // If Utilities (parent or any child) selected, or Taxes, open Utility/Linkage modal
      const isUtilitiesOrTaxes = (slug) => {
        if (String(slug) === 'taxes' || String(slug) === 'home_tax_payment') return true;
        try {
          const tree = expenseCategories || { items: [] };
          for (const it of (tree.items || [])) {
            if (String(it.value) === 'utilities' && Array.isArray(it.children)) {
              if (String(slug) === 'utilities') return true;
              if (it.children.some(ch => String(ch.value) === String(slug))) return true;
            }
          }
        } catch (e) { /* ignore */ }
        const KNOWN = new Set(['utilities','electric_gas','water','gas','electric','electricity','utilities__water','utilities__gas','utilities__electric','utilities__other']);
        return KNOWN.has(String(slug));
      };
      if (isUtilitiesOrTaxes(category)) {
        const txn = rawTransactions.find(t => t.id === transactionId);
        if (txn) {
          setUtilityRow({
            id: txn.id,
            sourceType: 'cash',
            date: txn.transaction_date,
            post_date: txn.post_date,
            description: txn.description,
            amount: txn.amount,
            // pass through any existing link data so the shared modal can prefill
            linked_asset_id: txn.linked_asset_id || '',
            linked_asset_expense_id: txn.linked_asset_expense_id || '',
            linked_utility_bill_id: txn.linked_utility_bill_id || '',
            linked_recurring_series_id: txn.linked_recurring_series_id || '',
            linked_property_id: txn.linked_property_id || '',
            // pass the just-selected utilities category for smarter defaults
            expense_category: category,
          });
          setShowUtilityModal(true);
        }
      }
      // If Debt Disbursement selected, open debt disbursement modal
      if (category === 'debt_disbursement') {
        const txn = rawTransactions.find(t => t.id === transactionId);
        if (txn) {
          setDebtDisbursementRow(txn);
          setShowDebtDisbursementModal(true);
        }
      }
      // If Payment selected, open payment modal
      if (category === 'payments') {
        setActivePaymentTxnId(transactionId);
        setSelectedDebt({ type: '', id: '' });
        setPaymentCandidates([]);
        setSelectedCandidateId('');
        setShowPaymentModal(true);
        try {
          setLoadingPayment(true);
          const res = await axios.get(`${apiBase}/transactions/${transactionId}/debts-for-payment/`);
          setDebtsForPayment(res.data.debts || []);
        } catch (e) {
          setDebtsForPayment([]);
        } finally {
          setLoadingPayment(false);
        }
      }
    } catch (err) { /* ignore */ }
  };

  const createNewStream = async () => {
    // Basic validation
    if (!incomeForm.newStream.source.trim()) {
      alert('Please provide a source for the new income stream.');
      return;
    }
    try {
      const res = await axios.post(`${apiBase}/transactions/income/streams/`, {
        source: incomeForm.newStream.source.trim(),
        description: incomeForm.newStream.description.trim(),
        start_date: incomeForm.newStream.start_date || null,
        end_date: incomeForm.newStream.end_date || null,
      });
      // Update list and select it
      const created = res.data;
      setIncomeStreams(prev => [created, ...prev]);
      setIncomeForm(prev => ({ ...prev, addNewStream: false, streamId: String(created.id) }));
    } catch (e) {
      alert('Failed to create income stream');
    }
  };

  const submitIncome = async () => {
    if (!activeIncomeTxnId) return;
    if (!incomeForm.source.trim()) {
      alert('Source is required');
      return;
    }
    const payload = {
      kind: incomeKind,
      source: incomeForm.source.trim(),
      description: incomeForm.description.trim(),
    };
    if (incomeKind === 'recurring') {
      if (!incomeForm.streamId) {
        alert('Please select an income stream');
        return;
      }
      payload.stream_id = parseInt(incomeForm.streamId);
    }
    try {
      await axios.post(`${apiBase}/transactions/${activeIncomeTxnId}/record-income/`, payload);
      setShowIncomeModal(false);
      setActiveIncomeTxnId(null);
      // Optional: refresh transactions
      await fetchTransactions();
    } catch (e) {
      alert('Failed to record income');
    }
  };

  // Payment linking helpers
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setActivePaymentTxnId(null);
    setSelectedDebt({ type: '', id: '' });
    setPaymentCandidates([]);
    setSelectedCandidateId('');
  };

  const onDebtChange = async (e) => {
    const value = e.target.value || '';
    if (!value) {
      setSelectedDebt({ type: '', id: '' });
      setPaymentCandidates([]);
      setSelectedCandidateId('');
      return;
    }
    try {
      const [type, idStr] = value.split('|');
      const idNum = parseInt(idStr);
      setSelectedDebt({ type, id: String(idNum) });
      if (!activePaymentTxnId) return;
      setLoadingPayment(true);
      const res = await axios.get(`${apiBase}/transactions/${activePaymentTxnId}/candidate-debt-payments/`, {
        params: { debt_type: type, debt_id: idNum }
      });
      setPaymentCandidates(res.data.candidates || []);
      setSelectedCandidateId('');
    } catch (err) {
      setPaymentCandidates([]);
      setSelectedCandidateId('');
    } finally {
      setLoadingPayment(false);
    }
  };

  const linkSelectedPayment = async () => {
    if (!activePaymentTxnId) return;
    if (!selectedDebt.type || !selectedDebt.id) {
      alert('Please select a debt to link to.');
      return;
    }
    if (!selectedCandidateId) {
      alert('Please select a matching payment candidate.');
      return;
    }
    try {
      setLoadingPayment(true);
      await axios.post(`${apiBase}/transactions/${activePaymentTxnId}/link-debt-payment/`, {
        debt_type: selectedDebt.type,
        debt_id: parseInt(selectedDebt.id),
        candidate_id: parseInt(selectedCandidateId)
      });
      // Refresh transactions and close
      await fetchTransactions();
      closePaymentModal();
      window.alert('Payment linked successfully.');
    } catch (e) {
      alert('Failed to link payment');
    } finally {
      setLoadingPayment(false);
    }
  };

  const toggleDropdown = (e, transactionId) => {
    e.stopPropagation();
    setOpenDropdownId(openDropdownId === transactionId ? null : transactionId);
  };

  const renderEditableCell = (transaction, field, displayValue, className = '') => {
    const isEditing = editingCell?.transactionId === transaction.id && editingCell?.field === field;
    if (isEditing) {
      return (
        <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type={field === 'transaction_date' || field === 'post_date' ? 'date' : field === 'amount' ? 'number' : 'text'}
            className="form-control form-control-sm"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyPress}
            autoFocus
            step={field === 'amount' ? '0.01' : undefined}
          />
          <button className="btn btn-sm btn-success" onClick={saveEdit} disabled={saving} title="Save (Enter)">{saving ? '...' : '✓'}</button>
          <button className="btn btn-sm btn-secondary" onClick={cancelEditing} disabled={saving} title="Cancel (Escape)">✗</button>
        </div>
      );
    }
    return (
      <div
        className={`editable-cell ${className}`}
        style={{ position: 'relative', cursor: 'pointer', padding: '4px', transition: 'background-color 0.2s' }}
        onMouseEnter={(e) => { e.target.style.backgroundColor = '#1f2937'; e.target.style.borderRadius = '4px'; const editIcon = e.target.querySelector('.edit-icon'); if (editIcon) editIcon.style.opacity = '1'; }}
        onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderRadius = '0'; const editIcon = e.target.querySelector('.edit-icon'); if (editIcon) editIcon.style.opacity = '0'; }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEditing(transaction.id, field, transaction[field]); }}
      >
        <span>{displayValue}</span>
        <span className="edit-icon" style={{ opacity: 0, marginLeft: '8px', fontSize: '12px', color: '#6c757d', transition: 'opacity 0.2s', pointerEvents: 'none' }}>✏️</span>
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownId(null);
      if (editingCell && !saving) { setEditingCell(null); setEditValue(''); }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [editingCell, saving]);

  if (loading) return <div className="page"><p>Loading...</p></div>;
  if (error) return <div className="page"><div className="alert alert-danger">{error}</div></div>;
  if (!account) return <div className="page"><p>Not found</p></div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Cash Account #{account.id}</h1>
        <Link to="/balance_sheet/assets/cash_assets" className="btn">Back to Cash Accounts</Link>
      </div>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        padding: '12px 16px',
        backgroundColor: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        marginBottom: '16px',
        color: '#e5e7eb',
        fontSize: '0.9rem'
      }}>
        <div><strong style={{ color: '#9ca3af' }}>Lender:</strong>&nbsp;<span>{account.lender_name || '—'}</span></div>
        <div><strong style={{ color: '#9ca3af' }}>Type:</strong>&nbsp;<span>{account.account_type}</span></div>
        <div><strong style={{ color: '#9ca3af' }}>Account #:</strong>&nbsp;<span>{account.account_number}</span></div>
        <div><strong style={{ color: '#9ca3af' }}>Opened:</strong>&nbsp;<span>{account.opened_date || '—'}</span></div>
      </div>

      <div className="card" style={{ marginTop: 16, backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }}>
        <div className="card-header" style={{ borderBottom: '1px solid #374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Balance Over Time</h3>
          <div style={{ fontSize: '0.9rem' }}>
            <span style={{ marginRight: '15px' }}><strong style={{ color: '#9ca3af' }}>Current:</strong> {formatCurrency(currentBalance)}</span>
            <span><strong style={{ color: '#9ca3af' }}>Average:</strong> {formatCurrency(averageBalance)}</span>
          </div>
        </div>
        <div className="card-body">
          <div className="d-flex gap-2 mb-3">
            {['1w', '1m', '3m', '6m', '12m', 'YTD', 'All'].map(p => (
              <button
                key={p}
                className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setPeriod(p)}
              >
                {p}
              </button>
            ))}
          </div>
          {seriesLoading && <div>Loading balance series...</div>}
          {seriesError && <div className="alert alert-warning">{seriesError}</div>}
          {!seriesLoading && !seriesError && (
            filteredSeries && filteredSeries.length > 0 ? (
              <div className="chart-card" style={{ height: '300px', width: '100%', margin: '0 auto', padding: '10px' }}>
                <Line
                  data={{
                    labels: filteredSeries.map(p => p.date),
                    datasets: [
                      {
                        label: 'Balance',
                        data: filteredSeries.map(p => p.balance),
                        borderColor: 'rgba(75,192,192,1)',
                        backgroundColor: 'rgba(75,192,192,0.2)',
                        tension: 0.2,
                        pointRadius: 0,
                        order: 2
                      },
                      {
                        label: 'Average',
                        data: filteredSeries.map(() => averageBalance),
                        borderColor: 'rgba(255, 99, 132, 0.8)',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        fill: false,
                        pointRadius: 0,
                        order: 1
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom', labels: { color: '#e5e7eb' } },
                      title: { display: false },
                      tooltip: { backgroundColor: '#1f2937', titleColor: '#e5e7eb', bodyColor: '#e5e7eb' }
                    },
                    scales: {
                      x: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
                      y: { 
                        ticks: { 
                          color: '#e5e7eb',
                          callback: function(value) {
                            return '$' + value.toLocaleString();
                          }
                        }, 
                        grid: { color: '#374151' } 
                      },
                    }
                  }}
                  height={300}
                />
              </div>
            ) : (
              <div>No series data available yet. Upload statements to populate balances.</div>
            )
          )}
        </div>
      </div>

      {showHomeModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
          <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(680px, 90vw)' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <h5 style={{ margin:0 }}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===homeTxnId); return (t && t.linked_asset_id) ? 'Edit Link — Home Expense' : 'Link Home Expense'; } catch(e){ return 'Link Home Expense'; } })()}</h5>
              <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                {(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===homeTxnId); return (t && t.linked_asset_id); } catch(e){ return false; } })() && (
                  <button
                    className="btn btn-sm btn-warning"
                    title="Unlink from asset"
                    onClick={async ()=>{
                      try {
                        await axios.post(`/api/balance_sheet/assets/cash_accounts/transactions/${homeTxnId}/unlink-asset-expense/`);
                        setShowHomeModal(false);
                        setHomeTxnId(null);
                        await fetchTransactions();
                      } catch(err){
                        console.error('Failed to unlink home expense', err);
                        setHomeError('Failed to unlink home expense.');
                      }
                    }}
                  >Unlink</button>
                )}
                <button className="btn btn-sm btn-secondary" onClick={()=> setShowHomeModal(false)}>✕</button>
              </div>
            </div>
            <div style={{ padding:16 }}>
              {homeError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{homeError}</div>}
              <div className="mb-3">
                <label className="form-label">Residential Property</label>
                <select className="form-select" value={homeSelectedPropertyId} onChange={(e)=> onChangePropertyForHome(e.target.value)} disabled={homeLoading}>
                  <option value="">Select property…</option>
                  {homeProperties.map(p => (<option key={p.id} value={p.id}>{p.address}</option>))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Structure</label>
                <select className="form-select" value={homeSelectedAssetId} onChange={(e)=> setHomeSelectedAssetId(e.target.value)} disabled={homeLoading || !homeSelectedPropertyId}>
                  <option value="">{homeSelectedPropertyId ? 'Select structure…' : 'Select property first'}</option>
                  {homeAssetOptions.map(opt => (<option key={opt.id} value={opt.id}>{opt.label}</option>))}
                </select>
                <small className="form-text text-muted">If no structures are available, please add a structure first.</small>
              </div>
              <div className="mb-3">
                <label className="form-check">
                  <input className="form-check-input" type="checkbox" checked={homeIsRecurring} onChange={e=> setHomeIsRecurring(e.target.checked)} />
                  <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                </label>
                {homeIsRecurring && (
                  <div style={{ marginTop:8 }}>
                    <div className="btn-group btn-group-sm" role="group" aria-label="Recurring mode">
                      <button type="button" className={`btn ${homeRecurringMode==='existing' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=>{ setHomeRecurringMode('existing'); if (homeRecurringChoice === '__new__') setHomeRecurringChoice(''); }}>
                        Existing
                      </button>
                      <button type="button" className={`btn ${homeRecurringMode==='new' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=>{ setHomeRecurringMode('new'); setHomeRecurringChoice('__new__'); }}>
                        New
                      </button>
                    </div>

                    {homeRecurringMode === 'existing' && (
                      <div style={{ marginTop:8 }}>
                        <label className="form-label">Existing recurring series</label>
                        <select
                          className="form-select"
                          value={homeRecurringChoice}
                          onChange={(e)=> setHomeRecurringChoice(e.target.value)}
                          disabled={!homeSelectedAssetId || homeRecurringLoading}
                          onFocus={async()=> { if (homeSelectedPropertyId && !homeSeriesOptions.length) {
                            try {
                              setHomeRecurringLoading(true);
                              const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${homeSelectedPropertyId}/recurring-series/`, { params: { include_children: 1, include_inactive: 1 } });
                              const items = Array.isArray(res.data) ? res.data.map(s => ({ id: String(s.id), name: s.name })) : [];
                              setHomeSeriesOptions(items);
                            } catch (e) { setHomeSeriesOptions([]); }
                            finally { setHomeRecurringLoading(false); }
                          } }}
                        >
                          <option value="">{homeRecurringLoading ? 'Loading…' : 'Select existing series…'}</option>
                          {homeSeriesOptions.map(opt => (
                            <option key={opt.id} value={String(opt.id)}>
                              {opt.name}
                            </option>
                          ))}
                        </select>
                        {!homeSeriesOptions.length && <small className="form-text text-muted">No existing recurring series found for this asset.</small>}
                      </div>
                    )}

                    {homeRecurringMode === 'new' && (
                      <div style={{ marginTop:8 }}>
                        <div className="mb-2">
                          <label className="form-label">New series name</label>
                          <input className="form-control" type="text" value={homeRecurringName} onChange={(e)=> setHomeRecurringName(e.target.value)} placeholder="e.g., Pest Control" />
                        </div>
                        <div className="mb-2">
                          <label className="form-label">Frequency</label>
                          <select className="form-select" value={homeFrequency} onChange={(e)=> setHomeFrequency(e.target.value)}>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="bi_monthly">Bi-monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="semi_annually">Semi-annually</option>
                            <option value="annually">Annually</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Series type</label>
                          <select className="form-select" value={homeRecurringType} onChange={(e)=> setHomeRecurringType(e.target.value)}>
                            <option value="fixed">Fixed</option>
                            <option value="variable">Variable</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-secondary" onClick={()=> setShowHomeModal(false)} disabled={homeLoading}>Cancel</button>
                <button className="btn btn-primary" onClick={submitHomeLink} disabled={homeLoading || !homeSelectedPropertyId || !homeSelectedAssetId || !homeUiCategory}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===homeTxnId); return (t && t.linked_asset_id) ? 'Save' : 'Link Expense'; } catch(e){ return 'Link Expense'; } })()}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLandModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
          <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(680px, 90vw)' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <h5 style={{ margin:0 }}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===landTxnId); return (t && t.linked_asset_id) ? 'Edit Link — Landscaping Expense' : 'Link Landscaping Expense'; } catch(e){ return 'Link Landscaping Expense'; } })()}</h5>
              <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                {(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===landTxnId); return (t && t.linked_asset_id); } catch(e){ return false; } })() && (
                  <button
                    className="btn btn-sm btn-warning"
                    title="Unlink from asset"
                    onClick={async ()=>{
                      try {
                        await axios.post(`/api/balance_sheet/assets/cash_accounts/transactions/${landTxnId}/unlink-asset-expense/`);
                        setShowLandModal(false);
                        setLandTxnId(null);
                        await fetchTransactions();
                      } catch(err){
                        console.error('Failed to unlink landscaping expense', err);
                        setLandError('Failed to unlink landscaping expense.');
                      }
                    }}
                  >Unlink</button>
                )}
                <button className="btn btn-sm btn-secondary" onClick={()=> setShowLandModal(false)}>✕</button>
              </div>
            </div>
            <div style={{ padding:16 }}>
              {landError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{landError}</div>}
              <div className="mb-3">
                <label className="form-label">Residential Property</label>
                <select className="form-select" value={selectedPropertyId} onChange={(e)=> onChangePropertyForLand(e.target.value)} disabled={landLoading}>
                  <option value="">Select property…</option>
                  {properties.map(p => (<option key={p.id} value={p.id}>{p.address}</option>))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Land</label>
                <div style={{ padding:8, border:'1px solid #374151', borderRadius:6, background:'#111827' }}>
                  {selectedPropertyId ? (
                    landLoading ? 'Loading land…' : (landChild ? (
                      landChild.label
                    ) : (
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                        <span>No Land configured for selected property.</span>
                        <button className="btn btn-sm btn-outline-info" onClick={()=> setShowAddLand(true)} disabled={landLoading}>Add Land</button>
                      </div>
                    ))
                  ) : (
                    <span style={{ color:'#9ca3af' }}>Select a property to see its Land.</span>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-check">
                  <input className="form-check-input" type="checkbox" checked={landIsRecurring} onChange={e=> setLandIsRecurring(e.target.checked)} />
                  <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                </label>
                {landIsRecurring && (
                  <div style={{ marginTop:8 }}>
                    <label className="form-label">Frequency</label>
                    <select className="form-select" value={landFrequency} onChange={e=> setLandFrequency(e.target.value)}>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="bi_monthly">Bi-monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semi_annually">Semi-annually</option>
                      <option value="annually">Annually</option>
                    </select>
                    <div style={{ marginTop:12 }}>
                      <label className="form-label">Recurring Series</label>
                      <select className="form-select" value={landRecurringChoice} onChange={(e)=> setLandRecurringChoice(e.target.value)}
                              disabled={!landChild || landRecurringLoading}
                              onFocus={async()=> { if (landChild && !landRecurringOptions.length) { await fetchLandRecurringOptions(landChild.id); } }}>
                        <option value="">{landRecurringLoading ? 'Loading…' : 'Select existing series…'}</option>
                        {landRecurringOptions.map(opt => (
                          <option key={opt.id} value={String(opt.id)}>
                            {opt.name} {opt.frequency ? `(${opt.frequency})` : ''}
                          </option>
                        ))}
                        <option value="__new__">Create new series…</option>
                      </select>
                      {landRecurringChoice === '__new__' && (
                        <div style={{ marginTop:8 }}>
                          <label className="form-label">New Series Name</label>
                          <input className="form-control" value={landRecurringName} onChange={e=> setLandRecurringName(e.target.value)} placeholder="e.g., Lawn Service" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-secondary" onClick={()=> setShowLandModal(false)} disabled={landLoading}>Cancel</button>
                <button className="btn btn-primary" onClick={submitLandLink} disabled={landLoading || !selectedPropertyId || !landChild}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===landTxnId); return (t && t.linked_asset_id) ? 'Save' : 'Link Expense'; } catch(e){ return 'Link Expense'; } })()}</button>
              </div>

              {showAddLand && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
                  <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(680px, 90vw)' }}>
                    <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                      <h5 style={{ margin:0 }}>Add Land</h5>
                      <button className="btn btn-sm btn-secondary" onClick={()=> setShowAddLand(false)} style={{ marginLeft:'auto' }}>✕</button>
                    </div>
                    <div style={{ padding:16 }}>
                      {landFormError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{landFormError}</div>}
                      <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                        <div>
                          <label className="form-label">Legal Description</label>
                          <input className="form-control" value={landForm.legal_description} onChange={e=> setLandForm(prev=> ({ ...prev, legal_description: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Parcel Number</label>
                          <input className="form-control" value={landForm.parcel_number} onChange={e=> setLandForm(prev=> ({ ...prev, parcel_number: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Lot Number</label>
                          <input className="form-control" type="number" value={landForm.lot_number} onChange={e=> setLandForm(prev=> ({ ...prev, lot_number: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Plat Number</label>
                          <input className="form-control" type="number" value={landForm.plat_number} onChange={e=> setLandForm(prev=> ({ ...prev, plat_number: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Tax District</label>
                          <input className="form-control" value={landForm.tax_district} onChange={e=> setLandForm(prev=> ({ ...prev, tax_district: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Area (acres)</label>
                          <input className="form-control" type="number" step="0.0001" value={landForm.area_in_acres} onChange={e=> setLandForm(prev=> ({ ...prev, area_in_acres: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Allocate Financing to Land (optional)</label>
                          <input className="form-control" type="number" step="0.01" value={landForm.financing_allocation} onChange={e=> setLandForm(prev=> ({ ...prev, financing_allocation: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Latitude (optional)</label>
                          <input className="form-control" type="number" step="0.000001" value={landForm.latitude} onChange={e=> setLandForm(prev=> ({ ...prev, latitude: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Longitude (optional)</label>
                          <input className="form-control" type="number" step="0.000001" value={landForm.longitude} onChange={e=> setLandForm(prev=> ({ ...prev, longitude: e.target.value }))} />
                        </div>
                        <div style={{ gridColumn:'1 / -1' }}>
                          <label className="form-label">Survey Description (optional)</label>
                          <textarea className="form-control" rows={3} value={landForm.survey_description} onChange={e=> setLandForm(prev=> ({ ...prev, survey_description: e.target.value }))} />
                        </div>
                      </div>
                      <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                        <button className="btn btn-secondary" onClick={()=> setShowAddLand(false)} disabled={landFormLoading}>Cancel</button>
                        <button className="btn btn-success" onClick={submitAddLand} disabled={landFormLoading || !selectedPropertyId || !landForm.legal_description || !landForm.parcel_number || !landForm.lot_number || !landForm.plat_number || !landForm.tax_district || !landForm.area_in_acres}>
                          {landFormLoading ? 'Saving…' : 'Save Land'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 16, backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }}>
        <div className="card-header" style={{ borderBottom: '1px solid #374151' }}>
          <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
            <h3 className="mb-0">Transactions</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                multiple
                onChange={(e) => {
                  const selected = Array.from(e.target.files || []);
                  if (!selected.length) return;
                  setFiles(selected);
                  onUpload(selected);
                  // reset to allow same file to be selected again
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                style={{ display: 'none' }}
              />
              <button
                className="btn btn-success"
                type="button"
                onClick={() => setShowCreateTxnModal(true)}
                title="Create a one-off transaction"
                style={{ padding: '8px 16px', fontWeight: 'bold' }}
              >
                + Transaction
              </button>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}
              >
                Upload Statements
              </button>
            </div>
          </div>
        </div>
        <div className="card-body">
          {txLoading && <div>Loading transactions...</div>}
          {txError && <div className="alert alert-warning">{txError}</div>}

          {/* Statement Selector */}
          <div className="row mb-4">
            <div className="col-md-4">
              <label htmlFor="statement-selector" className="form-label fw-semibold" style={{ color: '#e5e7eb' }}>Filter by Statement:</label>
              <select
                id="statement-selector"
                className="form-select"
                value={selectedStatement || ''}
                onChange={(e) => { setSelectedStatement(e.target.value); fetchTransactions(e.target.value); }}
                style={{ backgroundColor: '#111827', color: '#e5e7eb', border: '1px solid #374151' }}
              >
                <option value="current">Current Period</option>
                {statements
                  .sort((a, b) => new Date(b.billing_cycle_end_date) - new Date(a.billing_cycle_end_date))
                  .map(statement => (
                    <option key={statement.id} value={statement.id}>
                      {statement.statement_month_int}/{statement.statement_year_int}
                      ({formatDate(statement.billing_cycle_start_date)} - {formatDate(statement.billing_cycle_end_date)})
                    </option>
                  ))}
              </select>
              <small className="form-text" style={{ color: '#9ca3af' }}>
                "Current Period" shows transactions after the latest statement. Select a statement to view that period.
              </small>
            </div>
          </div>


          {/* Pie Chart */}
          {!txLoading && !txError && (
            <div className="mb-4">
              <TransactionsPieChart
                transactions={chartTransactions}
                negateForExpenses={true}
                includePayments={true}
                excludedCategories={["income_nonrecurring","income_recurring"]}
                enforceExpenseSign={true}
                excludeTransfers={true}
              />
            </div>
          )}

          {/* Transactions Table */}

          {/* Create Transaction Modal */}
          {showCreateTxnModal && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
              <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(640px, 92vw)' }}>
                <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                  <h5 style={{ margin:0 }}>New Transaction</h5>
                  <button className="btn btn-sm btn-secondary" onClick={()=> setShowCreateTxnModal(false)} style={{ marginLeft:'auto' }}>✕</button>
                </div>
                <div style={{ padding:16 }}>
                  {createTxnError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{createTxnError}</div>}
                  <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <div>
                      <label className="form-label">Transaction Date</label>
                      <input type="date" className="form-control" value={createTxnForm.transaction_date} onChange={e=> setCreateTxnForm(prev=> ({ ...prev, transaction_date: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label">Post Date (optional)</label>
                      <input type="date" className="form-control" value={createTxnForm.post_date} onChange={e=> setCreateTxnForm(prev=> ({ ...prev, post_date: e.target.value }))} />
                    </div>
                    <div style={{ gridColumn:'1 / -1' }}>
                      <label className="form-label">Description</label>
                      <input className="form-control" value={createTxnForm.description} onChange={e=> setCreateTxnForm(prev=> ({ ...prev, description: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label">Amount</label>
                      <input type="number" step="0.01" className="form-control" value={createTxnForm.amount} onChange={e=> setCreateTxnForm(prev=> ({ ...prev, amount: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label">Type</label>
                      <select className="form-select" value={createTxnForm.direction} onChange={e=> setCreateTxnForm(prev=> ({ ...prev, direction: e.target.value }))}>
                        <option value="debit">Debit (money out)</option>
                        <option value="deposit">Deposit (money in)</option>
                      </select>
                    </div>
                    <div style={{ gridColumn:'1 / -1' }}>
                      <label className="form-label">Expense Category</label>
                      <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr', gap:8 }}>
                        <select className="form-select" value={createTxnForm.expense_category}
                                onChange={e=> setCreateTxnForm(prev=> ({ ...prev, expense_category: e.target.value }))}>
                          {/* None option */}
                          <option value={expenseCategories.noneItem?.value || 'none'}>
                            {expenseCategories.noneItem?.label || 'None'}
                          </option>
                          {/* Parent/child options */}
                          {(expenseCategories.items || []).map((p) => (
                            <React.Fragment key={p.value}>
                              <option value={p.value}>{p.label}</option>
                              {(p.children || []).map((ch) => (
                                <option key={ch.value} value={ch.value}>
                                  {`  └ ${ch.label}`}
                                </option>
                              ))}
                            </React.Fragment>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                    <button className="btn btn-secondary" onClick={()=> setShowCreateTxnModal(false)} disabled={creatingTxn}>Cancel</button>
                    <button className="btn btn-success" onClick={submitCreateTransaction} disabled={creatingTxn}>
                      {creatingTxn ? 'Saving…' : 'Save Transaction'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showUtilityModal && utilityRow && (
            <AssetExpenseLinkModal
              visible={showUtilityModal}
              onClose={()=> { setShowUtilityModal(false); setUtilityRow(null); }}
              sourceType="cash"
              transaction={utilityRow}
              domain={getDomainForCategory(utilityRow.expense_category)}
              // No property preselected on CashAccountDetail; let modal show Property -> Structure pickers
              mediaConfig={{
                categories: [
                  { value: 'media_internet', label: 'Internet' },
                  { value: 'media_cable_tv', label: 'Cable/TV' },
                  { value: 'media_phone', label: 'Phone' },
                  { value: 'media_streaming', label: 'Streaming Services' },
                  { value: 'media_books', label: 'Books' },
                  { value: 'media_other', label: 'Other Media' },
                ],
              }}
              utilitiesConfig={{
                propertyId: null,
                categories: [
                  { value: 'utilities_electric_gas', label: 'Electric & Gas' },
                  { value: 'utilities_water', label: 'Water' },
                  { value: 'utilities_trash', label: 'Trash/Waste' },
                  { value: 'utilities_sewer', label: 'Sewer' },
                  { value: 'utilities_internet', label: 'Internet' },
                  { value: 'utilities_cable_tv', label: 'Cable/TV' },
                  { value: 'utilities_phone', label: 'Phone' },
                ]
              }}
              // If a previous link exists, prefill the asset selection
              initialValues={{
                asset_id: utilityRow.linked_asset_id || '',
                // Provide current utilities sub-category for precise prefill
                expense_category: utilityRow.expense_category || '',
                // Derive property id from the linked asset so the modal can preselect the Property
                property_id: (allAssets && utilityRow.linked_asset_id) ? (()=> {
                  try {
                    const aid = utilityRow.linked_asset_id;
                    const asset = allAssets.find(a => String(a.id) === String(aid));
                    if (!asset) return '';
                    const t2 = String(asset.asset_type_2 || asset.type_2 || asset.type2 || '').toLowerCase();
                    if (t2 === 'resi_property') return String(asset.id);
                    if (t2 === 'structure' || t2 === 'land') {
                      const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent || null;
                      const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
                      return rpId ? String(rpId) : '';
                    }
                    return '';
                  } catch(e){ return ''; }
                })() : '',
                // Pass through existing utilities linkage hints for preselection
                recurring_series_id: utilityRow.linked_recurring_series_id || '',
                utility_bill_id: utilityRow.linked_utility_bill_id || '',
              }}
              // After a successful link/unlink, refresh transactions and close
              onSaved={async (payload)=> { 
                await fetchTransactions(); 
                setShowUtilityModal(false); 
                setUtilityRow(null); 
              }}
              onUnlinked={async ()=> { 
                await fetchTransactions(); 
                setShowUtilityModal(false); 
                setUtilityRow(null); 
              }}
            />
          )}
          {showIncomeSplitModal && incomeSplitRow && (
            <IncomeSplitModal
              row={incomeSplitRow}
              defaultKind={incomeSplitDefaultKind}
              onClose={()=> { setShowIncomeSplitModal(false); setIncomeSplitRow(null); }}
              onCompleted={async ()=> { await fetchTransactions(); }}
            />
          )}
          {showIncomeLinkModal && incomeLinkRow && (
            <IncomeLinkModal
              row={incomeLinkRow}
              onClose={() => { setShowIncomeLinkModal(false); setIncomeLinkRow(null); }}
              onLinked={async () => { await fetchTransactions(); }}
            />
          )}
          {showDebtDisbursementModal && debtDisbursementRow && (
            <DebtDisbursementLinkModal
              row={debtDisbursementRow}
              onClose={() => { setShowDebtDisbursementModal(false); setDebtDisbursementRow(null); }}
              onLinked={async () => { await fetchTransactions(); }}
            />
          )}
          <ReceiptModal
            isOpen={showReceiptModal}
            onClose={closeReceipt}
            url={receiptPreviewUrl}
            title={receiptModalTitle}
            onDelete={deleteReceipt}
          />
          <div className="table-responsive" style={{ overflowX: 'visible' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems: 'center', marginBottom: 6 }}>
              <button 
                onClick={handleExport} 
                className="btn btn-outline-success btn-sm"
                title="Export to Excel"
              >
                <ExcelIcon /> Export
              </button>
              <div className="btn-group btn-group-sm" role="group" aria-label="Txn collapse">
                <button type="button" className="btn btn-outline-secondary" onClick={toggleAllTxnParents}>
                  {parentRowKeysWithChildren.length > 0 && parentRowKeysWithChildren.every(k => expandedTxns.has(k)) ? 'Collapse All' : 'Expand All'}
                </button>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', color: '#e5e7eb' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #374151' }}>
                  <th scope="col" style={{ cursor: 'pointer', padding: '12px 8px', textAlign: 'left' }} onClick={() => handleSort('transaction_date')}>
                    Transaction Date <SortIcon field="transaction_date" />
                  </th>
                  <th scope="col" style={{ cursor: 'pointer', padding: '12px 8px', textAlign: 'left' }} onClick={() => handleSort('post_date')}>
                    Post Date <SortIcon field="post_date" />
                  </th>
                  <th scope="col" style={{ cursor: 'pointer', padding: '12px 8px', textAlign: 'left' }} onClick={() => handleSort('description')}>
                    Description <SortIcon field="description" />
                  </th>
                  <th scope="col" style={{ cursor: 'pointer', padding: '12px 8px', textAlign: 'left' }} onClick={() => handleSort('user_description')}>
                    User Description <SortIcon field="user_description" />
                  </th>
                  <th scope="col" style={{ cursor: 'pointer', padding: '12px 8px', textAlign: 'left' }} onClick={() => handleSort('amount')}>
                    Amount <SortIcon field="amount" />
                  </th>
                  <th scope="col" style={{ cursor: 'pointer', minWidth:'220px', whiteSpace:'nowrap', padding: '12px 8px', textAlign: 'left' }} onClick={() => handleSort('expense_category')}>
                    Category <SortIcon field="expense_category" />
                  </th>
                  <th scope="col" style={{ padding: '12px 8px', textAlign: 'left' }}>Asset Link</th>
                  <th scope="col" style={{ padding: '12px 8px', textAlign: 'left' }}>Linked To-Do</th>
                  <th scope="col" style={{ padding: '12px 8px', textAlign: 'left' }}>Recurring Series</th>
                  <th scope="col" style={{ padding: '12px 8px', textAlign: 'left' }}>Debt Link</th>
                  <th scope="col" style={{ padding: '12px 8px', textAlign: 'left' }}>Receipt</th>
                  <th scope="col" style={{ padding: '12px 8px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {processedTableRows.length > 0 ? (
                  processedTableRows.map((transaction) => (
                    <tr key={transaction._isChild ? `child-${transaction.id}` : `parent-${transaction.id}`}
                        style={{
                          borderBottom: '1px solid #374151',
                          backgroundColor: transaction._isChild ? '#1f2a44' : 'transparent',
                          borderLeft: transaction._isChild ? '3px solid #60a5fa' : 'none'
                        }}>
                      <td style={{ padding: '8px' }}>
                        {renderEditableCell(
                          transaction,
                          'transaction_date',
                          transaction.transaction_date ? formatDate(transaction.transaction_date) : ''
                        )}
                      </td>
                      <td style={{ padding: '8px' }}>
                        {renderEditableCell(
                          transaction,
                          'post_date',
                          transaction.post_date ? formatDate(transaction.post_date) : ''
                        )}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          {/* Expand/collapse toggle for parents with children */}
                          {(!transaction._isChild && transaction.has_children) ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                const key = `cash-${transaction.id}`;
                                setExpandedTxns(prev => {
                                  const n = new Set([...prev]);
                                  if (n.has(key)) n.delete(key); else n.add(key);
                                  return n;
                                });
                              }}
                              title={expandedTxns.has(`cash-${transaction.id}`) ? 'Collapse' : 'Expand'}
                              style={{ padding:'2px 6px' }}
                            >
                              {expandedTxns.has(`cash-${transaction.id}`) ? '−' : '+'}
                            </button>
                          ) : (
                            <span style={{ width: 26 }}></span>
                          )}
                          {/* Indent child descriptions */}
                          <div style={transaction._isChild ? { paddingLeft: 12 } : {}}>
                            {renderEditableCell(
                              transaction,
                              'description',
                              transaction.description || ''
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        {renderEditableCell(
                          transaction,
                          'user_description',
                          transaction.user_description || ''
                        )}
                      </td>
                      <td style={{ padding: '8px' }}>
                        {renderEditableCell(
                          transaction,
                          'amount',
                          formatCurrency(transaction.amount || 0)
                        )}
                      </td>
                      <td style={{ minWidth:'220px', whiteSpace:'nowrap', padding: '8px' }}>
                        {/* Expense category display / dropdown */}
                        {(() => {
                          const category = transaction.expense_category || 'none';
                          // For synthetic children (split components), honor read-only and show label only
                          const isSyntheticChild = !!transaction._isChild && String(transaction.id || '').startsWith('synth-');
                          if (isSyntheticChild) {
                            const label = transaction.category_label || category || 'None';
                            return (
                              <span
                                style={{
                                  display:'inline-block',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  padding: '4px 8px',
                                  backgroundColor: '#1f2937',
                                  color: '#e5e7eb',
                                  border: '1px solid #374151'
                                }}
                              >
                                {label}
                              </span>
                            );
                          }
                          const tree = expenseCategories;
                          const label = categoryLabelMap.get(String(category)) || (tree.noneItem?.label || 'None');
                          const isOpen = openDropdownId === transaction.id;
                          const style = getCategoryStyle(category);
                          const isParentExpanded = (parentVal) => {
                            const set = expandedParents[transaction.id];
                            return !!(set && set.has(parentVal));
                          };
                          const toggleParentExpand = (parentVal) => {
                            setExpandedParents(prev => {
                              const cur = new Set(prev[transaction.id] || []);
                              if (cur.has(parentVal)) cur.delete(parentVal); else cur.add(parentVal);
                              return { ...prev, [transaction.id]: cur };
                            });
                          };
                          return (
                            <div className="position-relative" style={{ display: 'inline-block' }}>
                              <button
                                className="btn btn-sm"
                                type="button"
                                onClick={(e) => toggleDropdown(e, transaction.id)}
                                style={{ backgroundColor: style.backgroundColor, color: style.color, borderRadius: '12px', fontSize: '0.75rem', padding: '4px 8px', cursor: 'pointer', border: `1px solid ${style.borderColor}`, whiteSpace:'nowrap' }}
                              >
                                {label} ▼
                              </button>
                              {isOpen && (
                                <div
                                  className="position-absolute"
                                  style={{ top: '100%', left: 0, zIndex: 1000, minWidth: '320px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.375rem', boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.3)', marginTop: 2 }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div style={{ maxHeight: '480px', overflowY: 'auto', padding: '4px 0' }}>
                                    {tree.items.map((it) => (
                                      <div key={it.value}>
                                        <button
                                          className="w-100 text-start"
                                          style={{ display: 'block', padding: '8px 16px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                          onClick={() => {
                                            if (Array.isArray(it.children) && it.children.length > 0) {
                                              toggleParentExpand(it.value);
                                            } else {
                                              updateExpenseCategory(transaction.id, String(it.value));
                                              setOpenDropdownId(null);
                                            }
                                          }}
                                        >
                                          {(Array.isArray(it.children) && it.children.length > 0) ? (isParentExpanded(it.value) ? '▼ ' : '► ') : ''}
                                          {it.label}
                                        </button>
                                        {(Array.isArray(it.children) && it.children.length > 0 && isParentExpanded(it.value)) && (
                                          <div>
                                            {it.children.map(ch => (
                                              <button
                                                key={ch.value}
                                                className="w-100 text-start"
                                                style={{ display: 'block', padding: '6px 16px 6px 28px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                                onClick={() => { updateExpenseCategory(transaction.id, String(ch.value)); setOpenDropdownId(null); }}
                                              >
                                                {ch.label}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    <div style={{ borderTop: '1px solid #374151', margin: '4px 0' }} />
                                    <button
                                      className="w-100 text-start"
                                      style={{ display: 'block', padding: '8px 16px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                      onClick={() => { updateExpenseCategory(transaction.id, String(tree.noneItem.value)); setOpenDropdownId(null); }}
                                    >
                                      {tree.noneItem.label || 'None'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                          {(() => {
                            const aid = transaction.linked_asset_id;
                            if (!aid) {
                              const cat = String(transaction.expense_category || '');
                              const domain = getDomainForCategory(cat);
                              const isYardWaste = cat.includes('yard_waste');
                              const isWaste = (cat.startsWith('waste_') || cat.includes('sanitation')) && !isYardWaste;
                              const isLinkable = domain !== 'generic' || isWaste || isYardWaste;
                              if (!isLinkable) return null;

                              let labelBtn = 'Link Asset';
                              if (domain === 'utilities') labelBtn = 'Link Utilities';
                              else if (domain === 'home') labelBtn = 'Link Home';
                              else if (domain === 'landscaping' || isYardWaste) labelBtn = 'Link Landscaping';
                              else if (domain === 'vehicle') labelBtn = 'Link Vehicle';
                              else if (isWaste) labelBtn = 'Link Waste';

                              return (
                                <button
                                  className="btn btn-link btn-sm"
                                  style={{ color:'#93c5fd', padding:0 }}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    setUtilityRow({
                                      id: transaction.id,
                                      sourceType: 'cash',
                                      date: transaction.transaction_date,
                                      post_date: transaction.post_date,
                                      description: transaction.description,
                                      amount: transaction.amount,
                                      linked_asset_id: transaction.linked_asset_id || '',
                                      linked_asset_expense_id: transaction.linked_asset_expense_id || '',
                                      linked_utility_bill_id: transaction.linked_utility_bill_id || '',
                                      linked_recurring_series_id: transaction.linked_recurring_series_id || '',
                                      linked_property_id: transaction.linked_property_id || '',
                                      expense_category: transaction.expense_category || '',
                                    });
                                    setShowUtilityModal(true);
                                  }}
                                >
                                  {labelBtn}
                                </button>
                              );
                            }
                            const asset = allAssets.find(a => String(a.id) === String(aid));
                            const url = buildAssetUrl(asset);
                            const cat = String(transaction.expense_category || '');
                            const domain = getDomainForCategory(cat);
                            const label = buildAssetLabel(asset, { ...assetMaps, transactionDomain: domain });

                            return (
                              <>
                                {url ? (
                                  <Link to={url} title={label} style={{ color:'#93c5fd', whiteSpace:'nowrap', background:'transparent', border:'none' }}>{label}</Link>
                                ) : (
                                  <span title={label} style={{ color:'#93c5fd' }}>{label}</span>
                                )}
                                <button
                                  className="btn btn-link btn-sm"
                                  style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    setUtilityRow({
                                      id: transaction.id,
                                      sourceType: 'cash',
                                      date: transaction.transaction_date,
                                      post_date: transaction.post_date,
                                      description: transaction.description,
                                      amount: transaction.amount,
                                      linked_asset_id: transaction.linked_asset_id || '',
                                      linked_asset_expense_id: transaction.linked_asset_expense_id || '',
                                      linked_utility_bill_id: transaction.linked_utility_bill_id || '',
                                      linked_recurring_series_id: transaction.linked_recurring_series_id || '',
                                      linked_property_id: transaction.linked_property_id || '',
                                      expense_category: transaction.expense_category || '',
                                    });
                                    setShowUtilityModal(true);
                                  }}
                                >
                                  Edit Link
                                </button>
                              </>
                            );
                          })()}
                        </span>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                          {(() => {
                            const todoInfo = transaction.linked_todo_info;
                            if (!todoInfo) return null;

                            let url = null;
                            if (todoInfo.asset_type === 'vehicle') {
                              url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                            } else if (todoInfo.property_id) {
                              url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                            }

                            const statusColor = todoInfo.status === 'completed' ? '#22c55e' : (todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af');
                            
                            const content = (
                              <span 
                                style={{ color: '#93c5fd', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.85rem' }}
                                title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}
                              >
                                {todoInfo.title}
                              </span>
                            );

                            return (
                              <>
                                {url ? (
                                  <Link to={url}>{content}</Link>
                                ) : (
                                  content
                                )}
                                <span style={{ fontSize: '10px', color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '4px', padding: '0 4px', marginLeft: '4px' }}>
                                  {todoInfo.status_display}
                                </span>
                              </>
                            );
                          })()}
                        </span>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                          {(() => {
                            const expInfo = transaction.linked_expense_info;
                            const incInfo = transaction.linked_income_info;
                            
                            if (expInfo && expInfo.recurring_name) {
                              const aid = transaction.linked_asset_id;
                              const asset = allAssets.find(a => String(a.id) === String(aid));
                              const assetUrl = buildAssetUrl(asset);
                              // e.g., navigate to physical asset page with series selected in URL if possible
                              // For now, link to physical asset page.
                              const url = assetUrl ? `${assetUrl}?series_id=${transaction.linked_recurring_series_id}` : null;
                              
                              return (
                                <>
                                  {url ? (
                                    <Link to={url} style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{expInfo.recurring_name}</Link>
                                  ) : (
                                    <span style={{ color:'#e5e7eb' }}>{expInfo.recurring_name}</span>
                                  )}
                                  <button
                                    className="btn btn-link btn-sm"
                                    style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setUtilityRow({
                                        ...transaction,
                                        sourceType: 'cash',
                                        date: transaction.transaction_date,
                                        linked_asset_expense_id: transaction.linked_asset_expense_id || '',
                                        linked_utility_bill_id: transaction.linked_utility_bill_id || '',
                                        linked_recurring_series_id: transaction.linked_recurring_series_id || '',
                                      });
                                      setShowUtilityModal(true);
                                    }}
                                  >
                                    Edit Link
                                  </button>
                                </>
                              );
                            } else if (incInfo && incInfo.display_name) {
                              const url = `/income?series_id=${transaction.linked_income_stream_id}`;
                              return (
                                <>
                                  <Link to={url} style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{incInfo.display_name}</Link>
                                  <button
                                    className="btn btn-link btn-sm"
                                    style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setIncomeLinkRow(transaction);
                                      setShowIncomeLinkModal(true);
                                    }}
                                  >
                                    Edit Link
                                  </button>
                                </>
                              );
                            } else if (String(transaction.expense_category || '') === 'tax_refund' && !transaction.linked_income_stream_id) {
                              return (
                                <button
                                  className="btn btn-link btn-sm"
                                  style={{ color:'#93c5fd', padding:0 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setIncomeLinkRow(transaction);
                                    setShowIncomeLinkModal(true);
                                  }}
                                >
                                  Link Income
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </span>
                      </td>
                      <td>
                        <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                          {(() => {
                            const info = transaction.linked_debt_info || transaction.linkedDebt;
                            if (info && (info.debt_label || info.debt_name)) {
                              let url = null;
                              if (info.type === 'auto_loan') url = `/balance_sheet/financing/auto_loans/${transaction.linkedDebtId || info.id}`;
                              else if (info.type === 'mortgage') url = `/balance_sheet/financing/mortgages/${transaction.linkedDebtId || info.id}`;
                              else if (info.type === 'credit_card' || info.type === 'revolver') url = `/balance_sheet/financing/revolvers/credit-cards/${transaction.linkedDebtId || info.id}`;
                              else if (info.type === 'heloc') url = `/balance_sheet/financing/revolvers/helocs/${transaction.linkedDebtId || info.id}`;

                              return (
                                <>
                                  {url ? (
                                    <Link
                                      to={url}
                                      style={{ color:'#93c5fd', whiteSpace:'nowrap', background:'transparent', border:'none' }}
                                      title={info.display_text || ''}
                                    >
                                      {info.debt_label || info.debt_name || '🔗 Debt'}
                                    </Link>
                                  ) : (
                                    <span style={{ color:'#93c5fd', whiteSpace:'nowrap' }} title={info.display_text || ''}>
                                      {info.debt_label || info.debt_name || '🔗 Debt'}
                                    </span>
                                  )}
                                  <button
                                    className="btn btn-link btn-sm"
                                    style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (String(transaction.expense_category || '') === 'debt_disbursement') {
                                        setDebtDisbursementRow(transaction);
                                        setShowDebtDisbursementModal(true);
                                      } else {
                                        // Open payment modal logic
                                        setActivePaymentTxnId(transaction.id);
                                        setSelectedDebt({ type: info.type || '', id: String(transaction.linkedDebtId || info.id || '') });
                                        setSelectedCandidateId(transaction.linkedDebtAmortizationId || '');
                                        setShowPaymentModal(true);
                                        (async () => {
                                          try {
                                            setLoadingPayment(true);
                                            const res = await axios.get(`${apiBase}/transactions/${transaction.id}/debts-for-payment/`);
                                            setDebtsForPayment(res.data.debts || []);
                                          } catch (e) {
                                            setDebtsForPayment([]);
                                          } finally {
                                            setLoadingPayment(false);
                                          }
                                        })();
                                      }
                                    }}
                                  >
                                    Edit Link
                                  </button>
                                </>
                              );
                            } else if (String(transaction.expense_category || '') === 'payments' || String(transaction.expense_category || '') === 'debt_disbursement') {
                              return (
                                <button
                                  className="btn btn-link btn-sm"
                                  style={{ color:'#93c5fd', padding:0 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (String(transaction.expense_category || '') === 'debt_disbursement') {
                                      setDebtDisbursementRow(transaction);
                                      setShowDebtDisbursementModal(true);
                                    } else {
                                      // Open payment modal logic
                                      setActivePaymentTxnId(transaction.id);
                                      setSelectedDebt({ type: '', id: '' });
                                      setPaymentCandidates([]);
                                      setSelectedCandidateId('');
                                      setShowPaymentModal(true);
                                      (async () => {
                                        try {
                                          setLoadingPayment(true);
                                          const res = await axios.get(`${apiBase}/transactions/${transaction.id}/debts-for-payment/`);
                                          setDebtsForPayment(res.data.debts || []);
                                        } catch (e) {
                                          setDebtsForPayment([]);
                                        } finally {
                                          setLoadingPayment(false);
                                        }
                                      })();
                                    }
                                  }}
                                >
                                  Link Debt
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </span>
                      </td>
                        <td>
                          {(transaction.has_receipt || transaction.receipt_url || transaction.receipt) ? (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openReceipt(transaction)}
                            >View</button>
                          ) : (
                            <label className="btn btn-sm btn-outline-secondary mb-0">
                              Upload
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                style={{ display:'none' }}
                                onChange={(e) => {
                                  const file = e.target.files && e.target.files[0];
                                  if (file) uploadReceipt(transaction.id, file);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          )}
                        </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                            onClick={() => {
                              setSplitRow({ ...transaction, sourceType: 'cash' });
                              setShowSplitModal(true);
                            }}
                          >
                            Split
                          </button>

                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => deleteTransaction(transaction.id)}
                            disabled={deleting.has(transaction.id)}
                            style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                          >
                            {deleting.has(transaction.id) ? (<><span className="spinner-border spinner-border-sm me-1" role="status"></span>Deleting...</>) : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center text-muted py-4">No transactions found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>


      <SplitTransactionModal
        isOpen={showSplitModal}
        transaction={splitRow}
        onClose={() => { setShowSplitModal(false); setSplitRow(null); }}
        onSaveSuccess={async () => {
          setShowSplitModal(false);
          setSplitRow(null);
          await fetchTransactions();
        }}
        formatCurrency={formatCurrency}
      />

      {showPaymentModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: '#0b1220', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, width: 'min(680px, 90vw)', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 style={{ margin: 0 }}>Link Payment</h5>
              <button className="btn btn-sm btn-secondary" onClick={closePaymentModal}>✕</button>
            </div>
            <div style={{ padding: 16 }}>
              <p className="text-muted" style={{ marginTop: 0, color: '#f1f5f9' }}>Select the debt this cash payment belongs to, then choose the matching payment entry.</p>

              <div className="mb-3">
                <label className="form-label" style={{ color: '#f1f5f9' }}>Debt</label>
                <select
                  className="form-select"
                  onChange={onDebtChange}
                  value={selectedDebt.type && selectedDebt.id ? `${selectedDebt.type}|${selectedDebt.id}` : ''}
                  style={{ backgroundColor: '#111827', color: '#e5e7eb', border: '1px solid #374151' }}
                >
                  <option value="">Select a debt...</option>
                  {debtsForPayment.map(d => (
                    <option key={`${d.type}-${d.id}`} value={`${d.type}|${d.id}`}>{d.label}</option>
                  ))}
                </select>
              </div>

              {loadingPayment && <div className="mb-2" style={{ color: '#f1f5f9' }}>Loading matching payments...</div>}

              {selectedDebt.type && !loadingPayment && (
                <div className="mb-3">
                  <label className="form-label" style={{ color: '#f1f5f9' }}>Matching Payments</label>
                  {paymentCandidates.length === 0 ? (
                    <div className="text-muted" style={{ color: '#f1f5f9' }}>No matches found for this debt and cash transaction.</div>
                  ) : (
                    <div style={{ border: '1px solid #374151', borderRadius: 6, maxHeight: 300, overflowY: 'auto' }}>
                      {paymentCandidates.map(c => (
                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #111827' }}>
                          <input type="radio" name="candidate" value={c.id} checked={String(selectedCandidateId) === String(c.id)} onChange={() => setSelectedCandidateId(String(c.id))} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>{c.description || (c.candidate_type === 'amortization_row' ? 'Amortization Schedule Payment' : 'Transaction')}</div>
                            <div className="text-muted" style={{ fontSize: 12, color: '#f1f5f9' }}>
                              {c.post_date || c.payment_date ? `Date: ${formatDate(c.post_date || c.payment_date)}` : ''}
                            </div>
                          </div>
                          <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(c.amount)}</div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-secondary" onClick={closePaymentModal} disabled={loadingPayment}>Cancel</button>
                <button className="btn btn-primary" onClick={linkSelectedPayment} disabled={loadingPayment || !selectedDebt.type || !selectedCandidateId}>
                  {loadingPayment ? 'Linking...' : 'Link Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAutoModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: '#0b1220', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, width: 'min(720px, 95vw)', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 style={{ margin: 0 }}>{(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===autoTxnId); return (t && t.linked_asset_id) ? 'Edit Link — Vehicle Expense' : 'Link Vehicle Expense'; } catch(e){ return 'Link Vehicle Expense'; } })()}</h5>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {(()=>{ try { const t = (rawTransactions||[]).find(x=>x.id===autoTxnId); return (t && t.linked_asset_id); } catch(e){ return false; } })() && (
                  <button
                    className="btn btn-sm btn-warning"
                    title="Unlink from asset"
                    onClick={async ()=>{
                      try {
                        // Unlink this cash transaction from any asset expense
                        await axios.post(`/api/balance_sheet/assets/cash_accounts/transactions/${autoTxnId}/unlink-asset-expense/`);
                        // Refresh transactions and close
                        await fetchTransactions();
                        setShowAutoModal(false);
                        setAutoTxnId(null);
                      } catch(err){
                        console.error('Failed to unlink vehicle expense', err);
                        setAutoForm(prev=> ({ ...prev, error:'Failed to unlink vehicle expense.' }));
                      }
                    }}
                  >Unlink</button>
                )}
                <button className="btn btn-sm btn-secondary" onClick={()=>{ setShowAutoModal(false); setAutoTxnId(null); }}>✕</button>
              </div>
            </div>
            <div style={{ padding: 16 }}>
              {autoForm.error && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{autoForm.error}</div>}
              <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label className="form-label">Vehicle</label>
                  <select className="form-select" value={autoForm.vehicleId} onChange={e=> onChangeVehicle(e.target.value)}>
                    <option value="">Select vehicle…</option>
                    {vehicles.map(v => (<option key={v.id} value={v.id}>{v.description || `Vehicle #${v.id}`}</option>))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Asset Expense Category</label>
                  <select className="form-select" value={autoForm.assetExpenseCategory} onChange={e=> setAutoForm(prev=> ({ ...prev, assetExpenseCategory:e.target.value }))}>
                    {(assetExpenseChoices.length ? assetExpenseChoices : [
                      { value:'maintenance', label:'Asset Maintenance' },
                      { value:'cleaning', label:'Cleaning' },
                      { value:'repair', label:'Asset Repair' },
                      { value:'enhancement', label:'Asset Enhancement' },
                      { value:'insurance', label:'Insurance Payment' },
                      { value:'taxes', label:'Tax Payment' }
                    ]).map(c => (<option key={c.value} value={c.value}>{c.label}</option>))}
                  </select>
                </div>
              </div>
              <div style={{ marginTop:12 }}>
                <label className="form-check">
                  <input className="form-check-input" type="checkbox" checked={autoForm.isRecurring} onChange={e=> setAutoForm(prev=> ({ ...prev, isRecurring:e.target.checked }))} />
                  <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                </label>
              </div>
              {autoForm.isRecurring && (
                <div style={{ marginTop:12, border:'1px solid #374151', borderRadius:6, padding:12 }}>
                  <div style={{ marginBottom:8 }}>
                    <label className="form-check" style={{ marginRight:12 }}>
                      <input className="form-check-input" type="radio" name="schedmode" checked={autoForm.scheduleMode==='existing'} onChange={()=> setAutoForm(prev=> ({ ...prev, scheduleMode:'existing' }))} />
                      <span className="form-check-label" style={{ marginLeft:8 }}>Link to existing schedule</span>
                    </label>
                    <label className="form-check" style={{ marginLeft:16 }}>
                      <input className="form-check-input" type="radio" name="schedmode" checked={autoForm.scheduleMode==='new'} onChange={()=> setAutoForm(prev=> ({ ...prev, scheduleMode:'new' }))} />
                      <span className="form-check-label" style={{ marginLeft:8 }}>Create new schedule</span>
                    </label>
                  </div>
                  {autoForm.scheduleMode==='existing' ? (
                    <div>
                      <label className="form-label">Maintenance Schedule</label>
                      <select className="form-select" value={autoForm.scheduleId} onChange={e=> setAutoForm(prev=> ({ ...prev, scheduleId:e.target.value }))}>
                        <option value="">Select…</option>
                        {schedules.map(s => (<option key={s.id} value={s.id}>{s.description} — expected {s.expected_maintenance_date}</option>))}
                      </select>
                    </div>
                  ) : (
                    <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                      <div>
                        <label className="form-label">Description</label>
                        <input className="form-control" type="text" value={autoForm.newSchedule.description} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule:{ ...prev.newSchedule, description:e.target.value } }))} />
                      </div>
                      <div>
                        <label className="form-label">Frequency</label>
                        <select className="form-select" value={autoForm.newSchedule.frequency} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule:{ ...prev.newSchedule, frequency:e.target.value } }))}>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="bi_monthly">Bi-monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="semi_annually">Semi-annually</option>
                          <option value="annually">Annually</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Expected Date</label>
                        <input className="form-control" type="date" value={autoForm.newSchedule.expected_date} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule:{ ...prev.newSchedule, expected_date:e.target.value } }))} />
                      </div>
                      <div>
                        <label className="form-label">Expected Amount</label>
                        <input className="form-control" type="number" step="0.01" value={autoForm.newSchedule.expected_amount} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule:{ ...prev.newSchedule, expected_amount:e.target.value } }))} />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                <button className="btn btn-secondary" onClick={()=> { setShowAutoModal(false); setAutoTxnId(null); }}>Cancel</button>
                <button className="btn btn-success" onClick={submitAutoLink} disabled={autoForm.loading || !autoForm.vehicleId}>
                  {autoForm.loading ? 'Linking…' : 'Link Expense'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CashAccountDetail;
